import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { useData } from 'vike-react/useData';
import { ArrowCounterClockwise, CaretDown, Check, EnvelopeSimple, LinkSimple, LinkedinLogo, Plus, ShareNetwork, WhatsappLogo, X, XLogo } from '@phosphor-icons/react';

import { Link } from '../../components/Link';
import { BreadcrumbNavigation } from '../../components/BreadcrumbNavigation';
import { JcCard } from '../../components/jc/JcCard';
import { ASHTON_ARTICLE_AUTHOR } from '../../utils/seo/ashton-article-author';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';
import { calculateTaxIceberg, type TaxIcebergResult } from '../../utils/salary/calculate-tax-iceberg';
import { FINNISH_MUNICIPAL_TAX_RATES_2026 } from '../../utils/salary/finnish-municipal-tax-rates-2026';
import { TAX_PARAMS_2026 } from '../../utils/salary/calculate-finnish-net-salary';
import { EMPLOYER_COST_PARAMS_2026, type EmployerSize } from '../../utils/salary/calculate-employer-cost';
import { CAPITAL_INCOME_PARAMS_2026 } from '../../utils/salary/calculate-capital-income';
import { POPULAR_MONTHLY_NETS, POPULAR_MONTHLY_SALARIES, buildTaxIcebergSlug, decodeTaxIcebergState, encodeTaxIcebergUrl, taxIcebergBasePath, taxIcebergLogicalPath, type SalaryMode, type TaxIcebergUrlState } from '../../utils/salary/tax-iceberg-url';
import { raiseOutcome, solveGrossForNet } from '../../utils/salary/solve-gross-for-net';
import { checkTaxCard } from '../../utils/salary/tax-card-check';
import { churchTaxRateFor } from '../../utils/salary/finnish-parish-tax-rates-2026';
import { rolesNearSalary } from '../../utils/salary/roles-near-salary';
import { taxDestinations, type DestinationKey } from '../../utils/salary/tax-destinations';
import { SOURCES } from './sources';
import type { Data } from './+data.server';
import { TAX_ICEBERG_PAGE_SCRIPT_ID, getTaxIcebergDataFromScript } from './parseTaxIcebergInitialScript';
import { BASE_URL } from '../../utils/constants/url-constants';
import { CHURCH_TAX_AVG_RATE_PCT, shareCopy, shareFactsFromResult } from '../../utils/salary/tax-iceberg-share';
import { TAX_YEAR, asCopyLocale, faqHeading, faqItems, glossary, pageCopy, salaryPageCopy, taxCardSection, type CopyFacts } from './seo-copy';
import { IcebergVisual, MAX_ICEBERGS, type IcebergLayer, type IcebergSpec } from '../../components/tax-iceberg/IcebergVisual';

const MUNICIPALITY_OPTIONS = Object.keys(FINNISH_MUNICIPAL_TAX_RATES_2026).sort((a, b) => a.localeCompare(b, 'fi'));

/** Church tax is set per parish (1.0 to 2.25%); this is roughly the national average and is what
 * the "church member" toggle applies. Source: Verohallinto, kirkollisvero. */

/* Blue-and-white input styling shared by every control in the toolbar so they read as one set. */
const ui = {
  card: 'rounded-[24px] border border-[#cfe6f3] bg-white p-3 shadow-[0_1px_3px_rgba(22,21,15,0.08)] dark:border-[#24506f] dark:bg-[#0b1f30]',
  label: 'mb-0.5 block text-[11px] font-bold uppercase tracking-wide text-[#4d7d99] dark:text-[#8fc0dd]',
  field:
    'flex items-center gap-2 rounded-xl border-2 border-[#bfe0f0] bg-[#f3fafd] px-3 transition-colors focus-within:border-[#3a92c2] dark:border-[#2a5b80] dark:bg-[#0f2a40] dark:focus-within:border-[#5fa9d4]',
  input: 'w-full min-w-0 bg-transparent text-jc-ink placeholder:text-[#7fa6bd] focus:outline-none dark:text-[#eaf6fb]',
  prefix: 'shrink-0 font-bold text-[#4d7d99] dark:text-[#8fc0dd]',
  suffix: 'shrink-0 text-xs font-semibold text-[#7fa6bd] dark:text-[#6f9cb8]',
  segmented: 'gap-0.5 rounded-xl border-2 border-[#bfe0f0] bg-[#f3fafd] p-0.5 dark:border-[#2a5b80] dark:bg-[#0f2a40]',
  segment: (active: boolean) =>
    `rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
      active ? 'bg-[#1c6493] text-white shadow-sm' : 'text-[#4d7d99] hover:text-[#1c6493] dark:text-[#8fc0dd] dark:hover:text-white'
    }`,
  dashedButton:
    'inline-flex items-center gap-1 rounded-xl border-2 border-dashed border-[#3a92c2]/50 px-3 py-1.5 text-sm font-semibold text-[#1c6493] transition-colors hover:bg-[#e3f2f9] dark:text-[#8fc0dd] dark:hover:bg-[#0f2a40]',
  iconButton: 'rounded-lg p-1 text-[#4d7d99] transition-colors hover:bg-[#e3f2f9] hover:text-[#1c6493] dark:hover:bg-[#123a55]',
  groupTitle: 'mb-2 text-xs font-bold text-[#1c6493] dark:text-[#8fc0dd]',
  menuItem:
    'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-[#1c6493] transition-colors hover:bg-[#e3f2f9] dark:text-[#8fc0dd] dark:hover:bg-[#0f2a40]',
};

const messages = defineMessages({
  breadcrumbParent: {
    id: 'taxIceberg.breadcrumb.parent',
    defaultMessage: 'Salary guide',
    description: 'Breadcrumb parent link label',
  },
  breadcrumbCurrent: {
    id: 'taxIceberg.breadcrumb.current',
    defaultMessage: 'Tax iceberg',
    description: 'Breadcrumb current page label',
  },
  title: {
    id: 'taxIceberg.title',
    defaultMessage: 'The tax iceberg: what your salary really costs, and what you actually keep',
    description: 'H1 for the tax iceberg calculator page',
  },
  intro: {
    id: 'taxIceberg.intro',
    defaultMessage:
      'Your payslip shows one number. What employing you really costs, and what you keep after tax and contributions, sit below the waterline. Enter a gross salary to see your own iceberg.',
    description: 'Intro paragraph for tax iceberg page',
  },
  salaryInputLabel: {
    id: 'taxIceberg.input.salary.label',
    defaultMessage: 'Gross salary',
    description: 'Label for the gross salary input field',
  },
  salaryPresetsLabel: {
    id: 'taxIceberg.input.salary.presets',
    defaultMessage: 'Pick a common salary',
    description: 'Accessible label of the button that opens the list of common salaries under the gross salary field',
  },
  salaryPresetNet: {
    id: 'taxIceberg.input.salary.presetNet',
    defaultMessage: '{amount} net',
    description: 'Net pay shown next to each salary in the common-salaries list; amount is a formatted euro figure',
  },
  salaryModeNet: {
    id: 'taxIceberg.input.salary.modeNet',
    defaultMessage: 'Net salary (target)',
    description: 'Tab above the salary field: enter the net you want in hand and the gross is solved',
  },
  salaryModeLabel: {
    id: 'taxIceberg.input.salary.modeLabel',
    defaultMessage: 'Enter a gross salary or a target net',
    description: 'Accessible name of the gross/net tab pair above the salary field',
  },
  salaryPresetGross: {
    id: 'taxIceberg.input.salary.presetGross',
    defaultMessage: '{amount} gross',
    description: 'In net mode, the gross needed shown next to each net in the common-salaries list',
  },
  netModeStat: {
    id: 'taxIceberg.netModeStat',
    defaultMessage: 'For {net} in hand you need {gross} gross.',
    description: 'Headline line in net mode: the solved gross for the target net',
  },
  perMonthWord: {
    id: 'taxIceberg.perMonthWord',
    defaultMessage: 'a month',
    description: 'Period word used inside sentences, e.g. "leaves you 287 EUR more a month"',
  },
  perYearWord: {
    id: 'taxIceberg.perYearWord',
    defaultMessage: 'a year',
    description: 'Period word used inside sentences, e.g. "leaves you 3,400 EUR more a year"',
  },
  raiseHeading: {
    id: 'taxIceberg.raise.heading',
    defaultMessage: 'What would a raise leave you?',
    description: 'Heading of the raise slider card',
  },
  raiseLabel: {
    id: 'taxIceberg.raise.sliderLabel',
    defaultMessage: 'Raise amount',
    description: 'Accessible label of the raise slider',
  },
  raiseResult: {
    id: 'taxIceberg.raise.result',
    defaultMessage: 'A {raise} raise leaves you {gain} more {period}: you keep {kept} of it.',
    description: 'Result sentence of the raise slider; period is "a month" or "a year"',
  },
  raiseEmployer: {
    id: 'taxIceberg.raise.employer',
    defaultMessage: 'It costs your employer {cost} more.',
    description: 'Second line of the raise result: extra employer cost including contributions',
  },
  taxCardHeading: {
    id: 'taxIceberg.taxCard.heading',
    defaultMessage: 'Is your tax card right?',
    description: 'Heading of the tax card (verokortti) check card',
  },
  taxCardIntro: {
    id: 'taxIceberg.taxCard.intro',
    defaultMessage: 'The income tax on this salary comes to {actual} of gross. Compare that with the withholding rate printed on your tax card.',
    description: 'Intro line of the tax card check',
  },
  taxCardRate: {
    id: 'taxIceberg.taxCard.rate',
    defaultMessage: 'Rate on your tax card',
    description: 'Label of the tax card base withholding rate input (perusprosentti)',
  },
  taxCardLimit: {
    id: 'taxIceberg.taxCard.limit',
    defaultMessage: 'Income limit',
    description: 'Label of the tax card annual income limit input (tuloraja)',
  },
  taxCardLimitHint: {
    id: 'taxIceberg.taxCard.limitHint',
    defaultMessage: '€ per year, optional',
    description: 'Hint under the income limit input',
  },
  taxCardExtra: {
    id: 'taxIceberg.taxCard.extra',
    defaultMessage: 'Additional rate',
    description: 'Label of the tax card additional rate input (lisäprosentti)',
  },
  taxCardExtraHint: {
    id: 'taxIceberg.taxCard.extraHint',
    defaultMessage: 'Above the limit',
    description: 'Hint under the additional rate input',
  },
  taxCardRefund: {
    id: 'taxIceberg.taxCard.refund',
    defaultMessage: 'At {card} your card withholds about {amountYear} a year too much ({amountMonth} a month): expect a refund.',
    description: 'Tax card result when the card rate is higher than the computed tax rate',
  },
  taxCardBackTax: {
    id: 'taxIceberg.taxCard.backTax',
    defaultMessage: 'At {card} your card withholds about {amountYear} a year too little ({amountMonth} a month): expect back taxes.',
    description: 'Tax card result when the card rate is lower than the computed tax rate',
  },
  taxCardOnTarget: {
    id: 'taxIceberg.taxCard.onTarget',
    defaultMessage: 'At {card} your card matches this salary: the difference is under €60 a year.',
    description: 'Tax card result when the card is within a few dozen euros of the computed tax',
  },
  taxCardNote: {
    id: 'taxIceberg.taxCard.note',
    defaultMessage:
      'Withholding covers taxes only; pension and unemployment contributions are deducted separately. Only the standard deductions are counted, so a card that includes your own deductions can be lower on purpose.',
    description: 'Fine print under the tax card check',
  },
  rolesHeading: {
    id: 'taxIceberg.roles.heading',
    defaultMessage: 'Roles that pay about {amount} a month',
    description: 'Heading of the list of roles whose median salary is near the entered salary',
  },
  rolesIntro: {
    id: 'taxIceberg.roles.intro',
    defaultMessage: 'National medians from disclosed salary ranges in live postings. Open a role for its range, cities and current openings.',
    description: 'Intro under the roles heading',
  },
  rolesPostings: {
    id: 'taxIceberg.roles.postings',
    defaultMessage: '{count, plural, one {# posting} other {# postings}}',
    description: 'Sample size next to a role median',
  },
  rolesAll: {
    id: 'taxIceberg.roles.all',
    defaultMessage: 'All roles with salary data',
    description: 'Link to the salary hub under the roles list',
  },
  accuracyNote: {
    id: 'taxIceberg.accuracyNote',
    defaultMessage: "Checked against Veronmaksajat's {year} wage-earner table: within €1 at 15 income levels from €20,000 to €200,000.",
    description: 'Accuracy statement shown under the inputs; {year} is the tax year',
  },
  popularNets: {
    id: 'taxIceberg.popularNets',
    defaultMessage: 'Or start from what you want in hand',
    description: 'Heading of the list of links to target-net pages',
  },
  popularNetLink: {
    id: 'taxIceberg.popularNets.link',
    defaultMessage: '{amount} net',
    description: 'Link label to a target-net page, e.g. "€3,000 net"',
  },
  destinationsHeading: {
    id: 'taxIceberg.destinations.heading',
    defaultMessage: 'Where the money goes',
    description: 'Heading of the section that attributes every tax and contribution to its recipient',
  },
  destinationsTotal: {
    id: 'taxIceberg.destinations.total',
    defaultMessage: '{total} {period} in taxes and contributions',
    description: 'Total leaving the iceberg; period is "a month" or "a year"',
  },
  destinationsIntro: {
    id: 'taxIceberg.destinations.intro',
    defaultMessage: 'Every euro below the waterline has one recipient by law. Open the municipality and the state to see what they spend it on.',
    description: 'Intro under the destinations heading',
  },
  destinationsLegend: {
    id: 'taxIceberg.destinations.legend',
    defaultMessage:
      'Dark part of a bar: withheld from your pay. Light part: paid by your employer on top. The municipal and state splits follow their published spending (municipal accounts 2025, state budget 2026); they are proportional, not earmarked.',
    description: 'Legend and caveat under the destinations bars',
  },
  destinationsAverageMunicipality: {
    id: 'taxIceberg.destinations.averageMunicipality',
    defaultMessage: 'the average municipality',
    description: 'Used in place of a municipality name when none is chosen',
  },
  destPensions: {
    id: 'taxIceberg.destinations.pensions',
    defaultMessage: 'Earnings-related pensions',
    description: 'Destination name',
  },
  destPensionsDesc: {
    id: 'taxIceberg.destinations.pensions.desc',
    defaultMessage: "Your and your employer's TyEL contributions, paid to pension insurers: they fund today's pensions and build your own pension record.",
    description: 'Destination description',
  },
  destEmploymentFund: {
    id: 'taxIceberg.destinations.employmentFund',
    defaultMessage: 'Employment Fund',
    description: 'Destination name (Työllisyysrahasto)',
  },
  destEmploymentFundDesc: {
    id: 'taxIceberg.destinations.employmentFund.desc',
    defaultMessage: 'Unemployment insurance contributions: earnings-related unemployment allowance, adult education allowance, pension accrual during unemployment.',
    description: 'Destination description',
  },
  destKela: {
    id: 'taxIceberg.destinations.kela',
    defaultMessage: 'Kela health insurance',
    description: 'Destination name',
  },
  destKelaDesc: {
    id: 'taxIceberg.destinations.kela.desc',
    defaultMessage: "Medical-care and daily-allowance contributions and the employer's health insurance contribution: sickness and parental allowances, reimbursements for medicines and private doctors.",
    description: 'Destination description',
  },
  destMunicipality: {
    id: 'taxIceberg.destinations.municipality',
    defaultMessage: 'Your municipality ({name})',
    description: 'Destination name; name is the municipality or "the average municipality"',
  },
  destMunicipalityDesc: {
    id: 'taxIceberg.destinations.municipality.desc',
    defaultMessage: 'Municipal income tax, spent by {name} on its own services.',
    description: 'Destination description',
  },
  destState: {
    id: 'taxIceberg.destinations.state',
    defaultMessage: 'The state',
    description: 'Destination name',
  },
  destStateDesc: {
    id: 'taxIceberg.destinations.state.desc',
    defaultMessage: 'State income tax{vat}, spent through the {year} state budget.',
    description: 'Destination description; vat is " and VAT" or empty',
  },
  destStateVat: {
    id: 'taxIceberg.destinations.state.vat',
    defaultMessage: ' and VAT',
    description: 'Inserted into the state description when VAT is counted (leading space)',
  },
  destParish: {
    id: 'taxIceberg.destinations.parish',
    defaultMessage: 'Your parish',
    description: 'Destination name',
  },
  destParishDesc: {
    id: 'taxIceberg.destinations.parish.desc',
    defaultMessage: 'Church tax to the Evangelical Lutheran parish of your municipality.',
    description: 'Destination description',
  },
  destYle: {
    id: 'taxIceberg.destinations.yle',
    defaultMessage: 'Yle',
    description: 'Destination name (public broadcaster)',
  },
  destYleDesc: {
    id: 'taxIceberg.destinations.yle.desc',
    defaultMessage: 'The Yle tax funds public broadcasting.',
    description: 'Destination description',
  },
  destInsurers: {
    id: 'taxIceberg.destinations.insurers',
    defaultMessage: 'Insurers',
    description: 'Destination name',
  },
  destInsurersDesc: {
    id: 'taxIceberg.destinations.insurers.desc',
    defaultMessage: "The employer's accident and group life insurance premiums.",
    description: 'Destination description',
  },
  muniEarlyChildhood: { id: 'taxIceberg.destinations.muni.earlyChildhood', defaultMessage: 'Early childhood and pre-primary education', description: 'Municipal spending purpose' },
  muniBasicEducation: { id: 'taxIceberg.destinations.muni.basicEducation', defaultMessage: 'Basic education (comprehensive school)', description: 'Municipal spending purpose' },
  muniUpperSecondary: { id: 'taxIceberg.destinations.muni.upperSecondary', defaultMessage: 'Upper secondary and vocational education', description: 'Municipal spending purpose' },
  muniCultureSport: { id: 'taxIceberg.destinations.muni.cultureSport', defaultMessage: 'Culture, libraries, sport and youth', description: 'Municipal spending purpose' },
  muniStreetsParksPlanning: { id: 'taxIceberg.destinations.muni.streetsParksPlanning', defaultMessage: 'Streets, parks, planning, housing and rescue', description: 'Municipal spending purpose' },
  muniEmploymentBusiness: { id: 'taxIceberg.destinations.muni.employmentBusiness', defaultMessage: 'Employment services and business development', description: 'Municipal spending purpose' },
  muniAdministrationOther: { id: 'taxIceberg.destinations.muni.administrationOther', defaultMessage: 'Administration and other', description: 'Municipal spending purpose' },
  stateHealthSocialCounties: { id: 'taxIceberg.destinations.state.healthSocialCounties', defaultMessage: 'Health and social care (wellbeing services counties)', description: 'State spending purpose' },
  stateSocialSecurity: { id: 'taxIceberg.destinations.state.socialSecurity', defaultMessage: 'Social security: Kela benefits and state pensions', description: 'State spending purpose' },
  stateMunicipalGrants: { id: 'taxIceberg.destinations.state.municipalGrants', defaultMessage: 'State grants to municipalities', description: 'State spending purpose' },
  stateEducation: { id: 'taxIceberg.destinations.state.education', defaultMessage: 'Education, science and culture', description: 'State spending purpose' },
  stateDefence: { id: 'taxIceberg.destinations.state.defence', defaultMessage: 'Defence', description: 'State spending purpose' },
  stateTransport: { id: 'taxIceberg.destinations.state.transport', defaultMessage: 'Transport and communications', description: 'State spending purpose' },
  stateEmploymentEconomy: { id: 'taxIceberg.destinations.state.employmentEconomy', defaultMessage: 'Employment and the economy', description: 'State spending purpose' },
  stateDebtInterest: { id: 'taxIceberg.destinations.state.debtInterest', defaultMessage: 'Interest on state debt', description: 'State spending purpose' },
  stateEu: { id: 'taxIceberg.destinations.state.eu', defaultMessage: 'EU membership contributions', description: 'State spending purpose' },
  stateAgriculture: { id: 'taxIceberg.destinations.state.agriculture', defaultMessage: 'Agriculture and forestry', description: 'State spending purpose' },
  stateSecurityJustice: { id: 'taxIceberg.destinations.state.securityJustice', defaultMessage: 'Police, rescue, border and justice', description: 'State spending purpose' },
  stateForeignAffairs: { id: 'taxIceberg.destinations.state.foreignAffairs', defaultMessage: 'Foreign affairs and development aid', description: 'State spending purpose' },
  stateEnvironment: { id: 'taxIceberg.destinations.state.environment', defaultMessage: 'Environment', description: 'State spending purpose' },
  stateAdministrationOther: { id: 'taxIceberg.destinations.state.administrationOther', defaultMessage: 'Administration and other', description: 'State spending purpose' },
  sourcesHeading: {
    id: 'taxIceberg.sources.heading',
    defaultMessage: 'Sources',
    description: 'Heading of the list of data sources inside Sources & methodology',
  },
  taxCardWhere: {
    id: 'taxIceberg.taxCard.where',
    defaultMessage: 'In OmaVero it is the base rate (perusprosentti) on your tax card.',
    description: 'Hint telling where to find the withholding rate',
  },
  taxCardLimitToggle: {
    id: 'taxIceberg.taxCard.limitToggle',
    defaultMessage: 'My card has an income limit',
    description: 'Button that reveals the income limit and additional rate inputs',
  },
  taxCardBarCard: {
    id: 'taxIceberg.taxCard.barCard',
    defaultMessage: 'Your tax card',
    description: 'Label of the bar showing the card rate',
  },
  taxCardBarSalary: {
    id: 'taxIceberg.taxCard.barSalary',
    defaultMessage: 'Tax on this salary',
    description: 'Label of the bar showing the computed tax rate',
  },
  periodYear: {
    id: 'taxIceberg.input.period.year',
    defaultMessage: 'Year',
    description: 'Toggle option: annual salary period',
  },
  periodMonth: {
    id: 'taxIceberg.input.period.month',
    defaultMessage: 'Month',
    description: 'Toggle option: monthly salary period',
  },
  municipalityLabel: {
    id: 'taxIceberg.input.municipality.label',
    defaultMessage: 'Municipality (for municipal tax rate)',
    description: 'Label for the municipality picker',
  },
  municipalityPlaceholder: {
    id: 'taxIceberg.input.municipality.placeholder',
    defaultMessage: 'National average',
    description: 'Placeholder for the municipality picker when none is selected',
  },
  employerSizeLabel: {
    id: 'taxIceberg.input.employerSize.label',
    defaultMessage: "Employer's total payroll (affects unemployment insurance rate)",
    description: 'Label for the employer-size toggle',
  },
  employerSizeUnknown: {
    id: 'taxIceberg.input.employerSize.unknown',
    defaultMessage: "Not sure",
    description: 'Employer size option: unknown, use blended average',
  },
  employerSizeSmall: {
    id: 'taxIceberg.input.employerSize.small',
    defaultMessage: 'Under €2.5M/year',
    description: 'Employer size option: small payroll',
  },
  employerSizeLarge: {
    id: 'taxIceberg.input.employerSize.large',
    defaultMessage: 'Over €2.5M/year',
    description: 'Employer size option: large payroll',
  },
  advancedOptionsToggle: {
    id: 'taxIceberg.input.advancedOptions.toggle',
    defaultMessage: 'Advanced options (municipality, employer size)',
    description: 'Toggle label to expand the advanced input options',
  },
  headlineStat: {
    id: 'taxIceberg.headlineStat',
    defaultMessage: 'It costs {trueEmployerCost} to employ you, but you only get to keep {netAfterVat} of it once tax and VAT are both counted.',
    description: 'Headline shareable stat, {trueEmployerCost} and {netAfterVat} are formatted currency amounts',
  },
  keepRateStat: {
    id: 'taxIceberg.keepRateStat',
    defaultMessage: 'You keep {rate} of what it actually costs to employ you.',
    description: '{rate} is a formatted percentage',
  },
  layerEmployerContributions: {
    id: 'taxIceberg.layer.employerContributions',
    defaultMessage: 'Employer contributions',
    description: 'Iceberg layer label',
  },
  layerEmployeeTax: {
    id: 'taxIceberg.layer.employeeTax',
    defaultMessage: 'Your income tax & contributions',
    description: 'Iceberg layer label',
  },
  layerVat: {
    id: 'taxIceberg.layer.vat',
    defaultMessage: 'VAT when you spend it',
    description: 'Iceberg layer label',
  },
  layerNetAfterVat: {
    id: 'taxIceberg.layer.netAfterVat',
    defaultMessage: 'What you actually keep',
    description: 'Iceberg layer label',
  },
  visualAriaLabel: {
    id: 'taxIceberg.visual.ariaLabel',
    defaultMessage: 'Tax icebergs floating in the sea in front of Helsinki. The visible tip is the pay you actually keep; the much larger mass below the waterline is tax and contributions.',
    description: 'Accessible description of the iceberg illustration',
  },
  compareAdd: {
    id: 'taxIceberg.compare.add',
    defaultMessage: 'Compare with another salary',
    description: 'Button that adds a second (third...) salary to draw side by side, up to five in total',
  },
  compareRemove: {
    id: 'taxIceberg.compare.remove',
    defaultMessage: 'Remove this salary from the comparison',
    description: 'Accessible label of the remove button on a comparison salary',
  },
  compareHint: {
    id: 'taxIceberg.compare.hint',
    defaultMessage: 'Icebergs are drawn to the same scale, so you can compare them directly.',
    description: 'Small note shown while two or more salaries are being compared',
  },
  icebergTitleMonth: {
    id: 'taxIceberg.iceberg.title.month',
    defaultMessage: '{amount} / month',
    description: 'Caption above each iceberg when comparing monthly salaries, {amount} is a formatted currency amount',
  },
  advancedToggle: {
    id: 'taxIceberg.advanced.toggle',
    defaultMessage: 'Advanced options: deductions, other income, employer, family',
    description: 'Label of the collapsible advanced-options section under the main inputs',
  },
  advancedGroupEmployer: {
    id: 'taxIceberg.advanced.group.employer',
    defaultMessage: 'Employer',
    description: 'Advanced options group heading',
  },
  advancedGroupPerson: {
    id: 'taxIceberg.advanced.group.person',
    defaultMessage: 'About you',
    description: 'Advanced options group heading',
  },
  advancedGroupDeductions: {
    id: 'taxIceberg.advanced.group.deductions',
    defaultMessage: 'Deductions',
    description: 'Advanced options group heading',
  },
  advancedGroupOtherIncome: {
    id: 'taxIceberg.advanced.group.otherIncome',
    defaultMessage: 'Other income',
    description: 'Advanced options group heading',
  },
  churchLabel: {
    id: 'taxIceberg.input.church.label',
    defaultMessage: 'Church member (church tax about {rate})',
    description: 'Label of the church-membership toggle, {rate} is a formatted percentage',
  },
  optionNo: {
    id: 'taxIceberg.input.option.no',
    defaultMessage: 'No',
    description: 'Generic toggle option',
  },
  optionYes: {
    id: 'taxIceberg.input.option.yes',
    defaultMessage: 'Yes',
    description: 'Generic toggle option',
  },
  ageLabel: {
    id: 'taxIceberg.input.age.label',
    defaultMessage: 'Age (affects pension and unemployment contributions)',
    description: 'Label of the optional age input',
  },
  agePlaceholder: {
    id: 'taxIceberg.input.age.placeholder',
    defaultMessage: 'Working age',
    description: 'Placeholder of the optional age input when left empty',
  },
  childrenLabel: {
    id: 'taxIceberg.input.children.label',
    defaultMessage: 'Minor children (raises the earned-income credit)',
    description: 'Label of the dependent-children input',
  },
  soleGuardianLabel: {
    id: 'taxIceberg.input.soleGuardian.label',
    defaultMessage: 'Sole guardian',
    description: 'Label of the sole-guardian toggle, shown when there are children',
  },
  unionFeesLabel: {
    id: 'taxIceberg.input.unionFees.label',
    defaultMessage: 'Union and unemployment fund fees',
    description: 'Label of the union fees input (fully deductible)',
  },
  commutingLabel: {
    id: 'taxIceberg.input.commuting.label',
    defaultMessage: 'Commuting costs (deductible above {ownLiability}, up to {max} a year)',
    description: 'Label of the commuting costs input; both values are formatted currency amounts',
  },
  dividendsLabel: {
    id: 'taxIceberg.input.dividends.label',
    defaultMessage: 'Dividends from listed companies',
    description: 'Label of the dividends input',
  },
  capitalGainsLabel: {
    id: 'taxIceberg.input.capitalGains.label',
    defaultMessage: 'Capital gains (net of purchase price)',
    description: 'Label of the capital gains input',
  },
  otherIncomeHint: {
    id: 'taxIceberg.input.otherIncome.hint',
    defaultMessage: 'Taxed as capital income at {lower} up to {threshold} and {upper} above; no employer cost. Your own company must first pay its 20% corporate tax, which is not shown here.',
    description: 'Hint under the other-income inputs; percentages and a currency amount, all formatted',
  },
  unitSuffixMonth: {
    id: 'taxIceberg.input.unit.month',
    defaultMessage: '/ month',
    description: 'Suffix inside money inputs when the Month toggle is active',
  },
  unitSuffixYear: {
    id: 'taxIceberg.input.unit.year',
    defaultMessage: '/ year',
    description: 'Suffix inside money inputs when the Year toggle is active',
  },
  headlineStatWithCapital: {
    id: 'taxIceberg.headlineStat.withCapital',
    defaultMessage: 'Your salary and investments add up to {total} before tax, but you only get to keep {netAfterVat} once tax and VAT are both counted.',
    description: 'Headline stat when dividends or capital gains were entered; both values are formatted currency amounts',
  },
  keepRateStatTotal: {
    id: 'taxIceberg.keepRateStat.total',
    defaultMessage: 'You keep {rate} of everything that comes in.',
    description: 'Keep-rate line when dividends or capital gains were entered, {rate} formatted percentage',
  },
  detailEmployeeCapitalTax: {
    id: 'taxIceberg.detail.employee.capitalTax',
    defaultMessage: 'Capital income tax on dividends and gains: {amount}',
    description: 'Detail-card line, capital income tax',
  },
  detailEmployeeUnionDeducted: {
    id: 'taxIceberg.detail.employee.unionDeducted',
    defaultMessage: 'Union fees deducted from taxable income: {amount}',
    description: 'Detail-card line, union fee deduction applied',
  },
  detailEmployeeCommutingDeducted: {
    id: 'taxIceberg.detail.employee.commutingDeducted',
    defaultMessage: 'Commuting deduction applied: {amount}',
    description: 'Detail-card line, commuting deduction applied',
  },
  summaryCapital: {
    id: 'taxIceberg.summary.capital',
    defaultMessage: 'Dividends and capital gains: {amount}',
    description: 'Comparison summary line, shown when other income was entered',
  },
  breakdownCapitalGross: {
    id: 'taxIceberg.breakdown.capitalGross',
    defaultMessage: 'Dividends and capital gains',
    description: 'Breakdown row label',
  },
  breakdownCapitalTax: {
    id: 'taxIceberg.breakdown.capitalTax',
    defaultMessage: 'Capital income tax',
    description: 'Breakdown row label',
  },
  breakdownTotalIn: {
    id: 'taxIceberg.breakdown.totalIn',
    defaultMessage: 'Everything that comes in before tax',
    description: 'Breakdown row label, true employer cost plus other income',
  },
  methodologyDeductions: {
    id: 'taxIceberg.methodology.deductions',
    defaultMessage: 'Deductions: union and unemployment-fund fees are deducted in full from earned income; commuting costs are deductible above the {ownLiability} own-liability share up to {max} a year (Verohallinto, 2026). Church tax uses the Evangelical Lutheran parish rate of the chosen municipality (Verohallinto, 2026 decision), or the {churchRate} national average when none is chosen.',
    description: 'Methodology bullet; currency amounts and a percentage, all formatted',
  },
  methodologyCapital: {
    id: 'taxIceberg.methodology.capital',
    defaultMessage: 'Dividends and capital gains: taxed as capital income at {lower} up to {threshold} of taxable capital income and {upper} above (Verohallinto, 2026). Dividends from listed companies are 85% taxable and 15% exempt. Dividends from an unlisted company you own: up to an 8% yield on your share of its net assets, 25% is taxable capital income for the first {cap} a year and 85% above that; the part beyond the 8% yield is 75% taxable earned income. The company\'s own 20% corporate tax, capital-income deductions and loss carry-forwards are not modelled.',
    description: 'Methodology bullet; percentages and a currency amount, all formatted',
  },
  detailToggleLabel: {
    id: 'taxIceberg.input.detail.label',
    defaultMessage: 'Iceberg strata',
    description: 'Label of the toggle that switches the iceberg between four layers and one band per tax',
  },
  detailToggleHint: {
    id: 'taxIceberg.input.detail.hint',
    defaultMessage: 'Show one band per tax and contribution',
    description: 'Hint under the iceberg strata toggle',
  },
  detailSimple: {
    id: 'taxIceberg.input.detail.simple',
    defaultMessage: 'Four layers',
    description: 'Iceberg strata option: the simple four-layer view',
  },
  detailFull: {
    id: 'taxIceberg.input.detail.full',
    defaultMessage: 'Every tax',
    description: 'Iceberg strata option: one band per tax and contribution',
  },
  advancedGroupPicture: {
    id: 'taxIceberg.advanced.group.picture',
    defaultMessage: 'Picture',
    description: 'Advanced options group heading for display settings',
  },
  employerSizeShort: {
    id: 'taxIceberg.input.employerSize.short',
    defaultMessage: "Employer's total payroll",
    description: 'Short label of the employer payroll-size toggle in the Advanced grid',
  },
  employerSizeHint: {
    id: 'taxIceberg.input.employerSize.hint',
    defaultMessage: 'Sets the unemployment-insurance rate',
    description: 'Hint under the employer payroll-size toggle',
  },
  ageShort: {
    id: 'taxIceberg.input.age.short',
    defaultMessage: 'Age',
    description: 'Short label of the age input',
  },
  ageHint: {
    id: 'taxIceberg.input.age.hint',
    defaultMessage: 'Pension and unemployment contributions depend on it',
    description: 'Hint under the age input',
  },
  childrenShort: {
    id: 'taxIceberg.input.children.short',
    defaultMessage: 'Minor children',
    description: 'Short label of the dependent-children input',
  },
  childrenHint: {
    id: 'taxIceberg.input.children.hint',
    defaultMessage: 'Raises the earned-income credit',
    description: 'Hint under the dependent-children input',
  },
  soleGuardianHint: {
    id: 'taxIceberg.input.soleGuardian.hint',
    defaultMessage: 'Doubles the per-child credit',
    description: 'Hint under the sole-guardian toggle',
  },
  churchShort: {
    id: 'taxIceberg.input.church.short',
    defaultMessage: 'Church member',
    description: 'Short label of the church-membership toggle',
  },
  churchHint: {
    id: 'taxIceberg.input.church.hint',
    defaultMessage: 'Adds church tax of about {rate}',
    description: 'Hint under the church-membership toggle, {rate} formatted percentage',
  },
  unionFeesHint: {
    id: 'taxIceberg.input.unionFees.hint',
    defaultMessage: 'Deducted in full from earned income',
    description: 'Hint under the union fees input',
  },
  commutingShort: {
    id: 'taxIceberg.input.commuting.short',
    defaultMessage: 'Commuting costs',
    description: 'Short label of the commuting costs input',
  },
  commutingHint: {
    id: 'taxIceberg.input.commuting.hint',
    defaultMessage: 'Deductible above {ownLiability}, up to {max} a year',
    description: 'Hint under the commuting costs input; both values formatted currency amounts',
  },
  dividendsHint: {
    id: 'taxIceberg.input.dividends.hint',
    defaultMessage: '85% taxable as capital income',
    description: 'Hint under the dividends input',
  },
  capitalGainsHint: {
    id: 'taxIceberg.input.capitalGains.hint',
    defaultMessage: 'Taxable in full as capital income',
    description: 'Hint under the capital gains input',
  },
  layerEmployerTyel: {
    id: 'taxIceberg.layer.employerTyel',
    defaultMessage: 'Employer pension (TyEL)',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployerUnemployment: {
    id: 'taxIceberg.layer.employerUnemployment',
    defaultMessage: "Employer's unemployment insurance",
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployerHealth: {
    id: 'taxIceberg.layer.employerHealth',
    defaultMessage: "Employer's health insurance",
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployerAccident: {
    id: 'taxIceberg.layer.employerAccident',
    defaultMessage: 'Accident and group life insurance',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployeePension: {
    id: 'taxIceberg.layer.employeePension',
    defaultMessage: 'Your pension contribution',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployeeUnemployment: {
    id: 'taxIceberg.layer.employeeUnemployment',
    defaultMessage: 'Your unemployment insurance',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerEmployeeDailyAllowance: {
    id: 'taxIceberg.layer.employeeDailyAllowance',
    defaultMessage: 'Daily allowance contribution',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerYle: {
    id: 'taxIceberg.layer.yle',
    defaultMessage: 'Yle tax',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerChurch: {
    id: 'taxIceberg.layer.church',
    defaultMessage: 'Church tax',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerMedicalCare: {
    id: 'taxIceberg.layer.medicalCare',
    defaultMessage: 'Medical care contribution',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerMunicipalTax: {
    id: 'taxIceberg.layer.municipalTax',
    defaultMessage: 'Municipal tax',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerStateTax: {
    id: 'taxIceberg.layer.stateTax',
    defaultMessage: 'State income tax',
    description: 'Iceberg stratum label in the every-tax view',
  },
  layerCapitalTax: {
    id: 'taxIceberg.layer.capitalTax',
    defaultMessage: 'Capital income tax',
    description: 'Iceberg stratum label in the every-tax view',
  },
  vatToggleLabel: {
    id: 'taxIceberg.input.vat.label',
    defaultMessage: 'Count VAT on what you spend',
    description: 'Label of the prominent VAT switch in the main input row',
  },
  vatFieldLabel: {
    id: 'taxIceberg.vatField.label',
    defaultMessage: 'VAT',
    description: 'Short label above the VAT switch in the toolbar',
  },
  vatToggleHint: {
    id: 'taxIceberg.input.vat.hint',
    defaultMessage: 'Adds the VAT you pay when you spend your net pay',
    description: 'Hint under the VAT switch label',
  },
  headlineStatNoVat: {
    id: 'taxIceberg.headlineStat.noVat',
    defaultMessage: 'It costs {trueEmployerCost} to employ you, but you only get to keep {netAfterVat} of it after tax.',
    description: 'Headline stat when VAT is not counted; both values are formatted currency amounts',
  },
  headlineStatWithCapitalNoVat: {
    id: 'taxIceberg.headlineStat.withCapital.noVat',
    defaultMessage: 'Your salary and investments add up to {total} before tax, but you only get to keep {netAfterVat} after tax.',
    description: 'Headline stat when other income was entered and VAT is not counted; both values are formatted currency amounts',
  },
  unlistedDividendsLabel: {
    id: 'taxIceberg.input.unlistedDividends.label',
    defaultMessage: 'Dividends from your own company',
    description: 'Label of the unlisted-company dividend input',
  },
  unlistedDividendsHint: {
    id: 'taxIceberg.input.unlistedDividends.hint',
    defaultMessage: 'Unlisted: 25% taxable up to {cap} a year within an 8% yield',
    description: 'Hint under the unlisted-company dividend input, {cap} formatted currency',
  },
  netAssetsLabel: {
    id: 'taxIceberg.input.netAssets.label',
    defaultMessage: "Your share of the company's net assets",
    description: 'Label of the optional net-assets input that sets the 8% yield limit',
  },
  netAssetsHint: {
    id: 'taxIceberg.input.netAssets.hint',
    defaultMessage: 'Optional; empty assumes the dividend stays within the 8% yield',
    description: 'Hint under the net-assets input',
  },
  detailEmployeeUnlistedEarned: {
    id: 'taxIceberg.detail.employee.unlistedEarned',
    defaultMessage: 'Own-company dividend beyond the 8% yield, taxed as earned income: {amount}',
    description: 'Detail-card line, the earned-income part of an unlisted dividend',
  },
  detailCapitalUnlistedSplit: {
    id: 'taxIceberg.detail.capital.unlistedSplit',
    defaultMessage: 'Own-company dividend: {light} lightly taxed (25%), {heavy} at 85%',
    description: 'Detail-card line, how the unlisted dividend was split; both formatted currency amounts',
  },
  investmentsTitle: {
    id: 'taxIceberg.investments.title',
    defaultMessage: 'Dividends & capital gains',
    description: 'Caption above the separate investments iceberg',
  },
  investmentsStat: {
    id: 'taxIceberg.investments.stat',
    defaultMessage: 'Your dividends and capital gains add {gross}, of which you keep {net}.',
    description: 'Second headline line when investments were entered; both formatted currency amounts',
  },
  layerInvestmentTax: {
    id: 'taxIceberg.layer.investmentTax',
    defaultMessage: 'Tax on dividends & gains',
    description: 'Investments iceberg stratum label (all tax on the investment income)',
  },
  layerInvestmentEarnedTax: {
    id: 'taxIceberg.layer.investmentEarnedTax',
    defaultMessage: 'Income tax on own-company dividend',
    description: 'Investments iceberg stratum label in the every-tax view: the earned-income tax caused by an unlisted dividend beyond the 8% yield',
  },
  breakdownInvestmentsTax: {
    id: 'taxIceberg.breakdown.investmentsTax',
    defaultMessage: 'Tax on dividends and gains',
    description: 'Breakdown row label',
  },
  breakdownInvestmentsKeep: {
    id: 'taxIceberg.breakdown.investmentsKeep',
    defaultMessage: 'You keep from dividends and gains',
    description: 'Breakdown row label',
  },
  summaryInvestmentsTax: {
    id: 'taxIceberg.summary.investmentsTax',
    defaultMessage: 'Tax on it: {amount}',
    description: 'Investments caption summary line',
  },
  summaryInvestmentsKeep: {
    id: 'taxIceberg.summary.investmentsKeep',
    defaultMessage: 'You keep {amount} ({rate} of it)',
    description: 'Investments caption summary line, {rate} formatted percentage of the investment income',
  },
  detailShareInvestments: {
    id: 'taxIceberg.detail.share.investments',
    defaultMessage: '{share} of your dividends and gains',
    description: 'Detail-card line under a layer amount on the investments iceberg, {share} formatted percentage',
  },
  detailCapitalUnlistedLight: {
    id: 'taxIceberg.detail.capital.unlistedLight',
    defaultMessage: 'Own-company dividend within the 8% yield: {light} taxed on 25%',
    description: 'Detail-card line when the whole unlisted dividend is lightly taxed, {light} formatted currency',
  },
  detailInvestmentsListed: {
    id: 'taxIceberg.detail.investments.listed',
    defaultMessage: 'Dividends from listed companies: {amount} (85% taxable)',
    description: 'Detail-card line on the investments iceberg',
  },
  detailInvestmentsGains: {
    id: 'taxIceberg.detail.investments.gains',
    defaultMessage: 'Capital gains: {amount} (taxable in full)',
    description: 'Detail-card line on the investments iceberg',
  },
  detailInvestmentsOwn: {
    id: 'taxIceberg.detail.investments.own',
    defaultMessage: 'Dividends from your own company: {amount}',
    description: 'Detail-card line on the investments iceberg',
  },
  detailInvestmentsGross: {
    id: 'taxIceberg.detail.investments.gross',
    defaultMessage: 'Dividends and gains before tax: {amount}',
    description: 'Detail-card line on the investments iceberg tip',
  },
  detailInvestmentsMinusTax: {
    id: 'taxIceberg.detail.investments.minusTax',
    defaultMessage: 'minus tax on them: {amount}',
    description: 'Detail-card line on the investments iceberg tip',
  },
  popularSalaries: {
    id: 'taxIceberg.popularSalaries',
    defaultMessage: 'See the iceberg for other salaries',
    description: 'Heading of the row of links to common monthly salaries at the bottom of the page',
  },
  popularSalaryLink: {
    id: 'taxIceberg.popularSalaries.link',
    defaultMessage: '{amount} / month',
    description: 'One link in the common-salaries row, {amount} formatted currency',
  },
  copyLink: {
    id: 'taxIceberg.copyLink',
    defaultMessage: 'Copy link',
    description: 'Button that copies the URL of the current view to the clipboard',
  },
  copied: {
    id: 'taxIceberg.copyLink.copied',
    defaultMessage: 'Copied',
    description: 'Copy-link button label right after copying',
  },
  resetOptions: {
    id: 'taxIceberg.resetOptions',
    defaultMessage: 'Reset options',
    description: 'Button that clears every option (municipality, VAT, deductions, comparisons...) but keeps the gross salary',
  },
  share: {
    id: 'taxIceberg.share',
    defaultMessage: 'Share',
    description: 'Button that opens the device share sheet (or a share menu) for the current view',
  },
  shareMenuLabel: {
    id: 'taxIceberg.share.menuLabel',
    defaultMessage: 'Share this iceberg',
    description: 'Accessible name of the share menu shown when the browser has no native share sheet',
  },
  shareOnX: {
    id: 'taxIceberg.share.x',
    defaultMessage: 'Share on X',
    description: 'Share menu item',
  },
  shareOnLinkedIn: {
    id: 'taxIceberg.share.linkedin',
    defaultMessage: 'Share on LinkedIn',
    description: 'Share menu item',
  },
  shareOnWhatsApp: {
    id: 'taxIceberg.share.whatsapp',
    defaultMessage: 'Share on WhatsApp',
    description: 'Share menu item',
  },
  shareByEmail: {
    id: 'taxIceberg.share.email',
    defaultMessage: 'Send by email',
    description: 'Share menu item',
  },
  taxRateStat: {
    id: 'taxIceberg.taxRateStat',
    defaultMessage: 'Your tax rate on this salary: {rate} (with pension and unemployment contributions: {total})',
    description: 'Stat line under the headline: the tax-card style tax rate, and the rate including employee contributions; both formatted percentages',
  },
  taxYearNote: {
    id: 'taxIceberg.taxYearNote',
    defaultMessage: 'Calculated with the {year} tax rules',
    description: 'Small freshness note next to the calculator, {year} is the tax year of the parameters in use',
  },
  amountsPerMonth: {
    id: 'taxIceberg.amounts.perMonth',
    defaultMessage: 'All amounts per month',
    description: 'Unit note shown with the iceberg legend and the breakdown table when the Month toggle is active',
  },
  amountsPerYear: {
    id: 'taxIceberg.amounts.perYear',
    defaultMessage: 'All amounts per year',
    description: 'Unit note shown with the iceberg legend and the breakdown table when the Year toggle is active',
  },
  toolbarMunicipality: {
    id: 'taxIceberg.toolbar.municipality',
    defaultMessage: 'Municipality',
    description: 'Short label above the municipality picker in the input toolbar',
  },
  toolbarEmployerSize: {
    id: 'taxIceberg.toolbar.employerSize',
    defaultMessage: "Employer's payroll",
    description: 'Short label above the employer payroll-size toggle in the input toolbar',
  },
  icebergTitleYear: {
    id: 'taxIceberg.iceberg.title.year',
    defaultMessage: '{amount} / year',
    description: 'Caption above each iceberg when comparing annual salaries, {amount} is a formatted currency amount',
  },
  detailShare: {
    id: 'taxIceberg.detail.share',
    defaultMessage: '{share} of what it costs to employ you',
    description: 'Detail-card line under a layer amount, {share} is a formatted percentage of the true employer cost',
  },
  detailEmployerTyel: {
    id: 'taxIceberg.detail.employer.tyel',
    defaultMessage: 'Pension (TyEL) {rate}: {amount}',
    description: 'Detail-card line, employer pension contribution, {rate} formatted percentage, {amount} currency',
  },
  detailEmployerUnemployment: {
    id: 'taxIceberg.detail.employer.unemployment',
    defaultMessage: 'Unemployment insurance: {amount}',
    description: 'Detail-card line, employer unemployment insurance contribution',
  },
  detailEmployerHealth: {
    id: 'taxIceberg.detail.employer.health',
    defaultMessage: 'Health insurance contribution: {amount}',
    description: 'Detail-card line, employer health insurance contribution',
  },
  detailEmployerAccident: {
    id: 'taxIceberg.detail.employer.accident',
    defaultMessage: 'Accident & group life insurance: {amount}',
    description: 'Detail-card line, employer accident and group life insurance',
  },
  detailEmployeeState: {
    id: 'taxIceberg.detail.employee.state',
    defaultMessage: 'State income tax: {amount}',
    description: 'Detail-card line, progressive state income tax',
  },
  detailEmployeeMunicipal: {
    id: 'taxIceberg.detail.employee.municipal',
    defaultMessage: 'Municipal tax ({rate}): {amount}',
    description: 'Detail-card line, municipal income tax, {rate} is the municipal rate as a formatted percentage',
  },
  detailEmployeeMedical: {
    id: 'taxIceberg.detail.employee.medical',
    defaultMessage: 'Medical care contribution: {amount}',
    description: 'Detail-card line, the sairaanhoitomaksu health-insurance contribution',
  },
  detailEmployeeYle: {
    id: 'taxIceberg.detail.employee.yle',
    defaultMessage: 'Yle tax: {amount}',
    description: 'Detail-card line, public broadcasting tax',
  },
  detailEmployeeChurch: {
    id: 'taxIceberg.detail.employee.church',
    defaultMessage: 'Church tax: {amount}',
    description: 'Detail-card line, church tax (only shown when non-zero)',
  },
  detailEmployeePension: {
    id: 'taxIceberg.detail.employee.pension',
    defaultMessage: 'Your pension contribution: {amount}',
    description: 'Detail-card line, employee TyEL pension contribution',
  },
  detailEmployeeUnemployment: {
    id: 'taxIceberg.detail.employee.unemployment',
    defaultMessage: 'Your unemployment insurance: {amount}',
    description: 'Detail-card line, employee unemployment insurance contribution',
  },
  detailEmployeeDailyAllowance: {
    id: 'taxIceberg.detail.employee.dailyAllowance',
    defaultMessage: 'Daily allowance contribution: {amount}',
    description: 'Detail-card line, the päivärahamaksu contribution (only shown when non-zero)',
  },
  detailEmployeeCredit: {
    id: 'taxIceberg.detail.employee.credit',
    defaultMessage: 'Earned-income tax credit: {amount}',
    description: 'Detail-card line, credit that reduces the tax bill, {amount} is shown as a negative currency amount',
  },
  detailVatAssumption: {
    id: 'taxIceberg.detail.vat.assumption',
    defaultMessage: 'Assumes {share} of net pay is spent, taxed at a blended VAT rate of {rate}',
    description: 'Detail-card line explaining the VAT estimate, both values formatted percentages',
  },
  detailVatBasis: {
    id: 'taxIceberg.detail.vat.basis',
    defaultMessage: 'Spending patterns from Statistics Finland by income level',
    description: 'Detail-card line naming the source of the VAT estimate',
  },
  detailNetPayslip: {
    id: 'taxIceberg.detail.net.payslip',
    defaultMessage: 'Net pay on your payslip: {amount}',
    description: 'Detail-card line, net pay before VAT',
  },
  detailNetMinusVat: {
    id: 'taxIceberg.detail.net.minusVat',
    defaultMessage: 'minus VAT paid when you spend it: {amount}',
    description: 'Detail-card line, VAT subtracted from net pay',
  },
  detailNetShareOfGross: {
    id: 'taxIceberg.detail.net.shareOfGross',
    defaultMessage: '{share} of your gross salary',
    description: 'Detail-card line, net after VAT as a share of gross, {share} formatted percentage',
  },
  summaryGross: {
    id: 'taxIceberg.summary.gross',
    defaultMessage: 'Gross salary: {amount}',
    description: 'Comparison summary line',
  },
  summaryTrueCost: {
    id: 'taxIceberg.summary.trueCost',
    defaultMessage: 'True cost to employer: {amount}',
    description: 'Comparison summary line',
  },
  summaryKeep: {
    id: 'taxIceberg.summary.keep',
    defaultMessage: 'You keep {amount} ({rate} of the true cost)',
    description: 'Comparison summary line, {rate} formatted percentage',
  },
  summaryDelta: {
    id: 'taxIceberg.summary.delta',
    defaultMessage: 'vs. the first salary: {deltaGross} gross, {deltaKept} kept',
    description: 'Comparison summary line shown on every iceberg except the first; both values are signed currency amounts in the selected period',
  },
  breakdownHeading: {
    id: 'taxIceberg.breakdown.heading',
    defaultMessage: 'Full breakdown',
    description: 'Section heading for the numeric breakdown table',
  },
  breakdownGross: {
    id: 'taxIceberg.breakdown.gross',
    defaultMessage: 'Gross salary',
    description: 'Breakdown row label',
  },
  breakdownTrueCost: {
    id: 'taxIceberg.breakdown.trueCost',
    defaultMessage: 'True cost to employer (gross + employer contributions)',
    description: 'Breakdown row label',
  },
  breakdownNetPay: {
    id: 'taxIceberg.breakdown.netPay',
    defaultMessage: 'Net pay',
    description: 'Breakdown row label: take-home pay before VAT is subtracted',
  },
  genericEstimateNoteBoth: {
    id: 'taxIceberg.genericEstimateNote.both',
    defaultMessage: 'Using the national-average municipal tax rate ({rate}) and a blended unemployment-insurance rate. Pick a municipality and employer payroll size above for a more precise figure.',
    description: 'Note shown when both municipality and employer size are unset, {rate} is a formatted percentage',
  },
  genericEstimateNoteMunicipal: {
    id: 'taxIceberg.genericEstimateNote.municipal',
    defaultMessage: 'Using the national-average municipal tax rate ({rate}). Pick your municipality above for a more precise figure.',
    description: 'Note shown when only municipality is unset, {rate} is a formatted percentage',
  },
  genericEstimateNoteEmployerSize: {
    id: 'taxIceberg.genericEstimateNote.employerSize',
    defaultMessage: "Using a blended unemployment-insurance rate. Pick your employer's payroll size above for a more precise figure.",
    description: 'Note shown when only employer size is unset',
  },
  methodologyHeading: {
    id: 'taxIceberg.methodology.heading',
    defaultMessage: 'Sources & methodology',
    description: 'Section heading for the sources and methodology block',
  },
  methodologyIntro: {
    id: 'taxIceberg.methodology.intro',
    defaultMessage: 'Every figure below traces to a named, checkable source. Nothing here is estimated without saying so.',
    description: 'Intro line for the methodology section',
  },
  methodologyEmployeeTax: {
    id: 'taxIceberg.methodology.employeeTax',
    defaultMessage: 'Income tax, municipal tax, church tax, Yle tax, employee pension and unemployment insurance, and the earned-income tax credit: computed per Verohallinto\'s (Finnish Tax Administration) 2026 withholding basis and Finlex\'s 2026 tax scale act.',
    description: 'Methodology bullet',
  },
  methodologyMunicipal: {
    id: 'taxIceberg.methodology.municipal',
    defaultMessage: 'Municipal tax rates for all 308 reporting municipalities: Suomen Kuntaliitto (Association of Finnish Municipalities), official 2026 rate table, sourced from Verohallinto.',
    description: 'Methodology bullet',
  },
  methodologyEmployerTyel: {
    id: 'taxIceberg.methodology.employerTyel',
    defaultMessage: "Employer pension contribution (TyEL), {tyelRate}: Eläketurvakeskus (Finnish Centre for Pensions), 2026 contribution rates. This rate became flat across all ages in the 2026 reform; before 2026 it was banded by employee age.",
    description: 'Methodology bullet, {tyelRate} is a formatted percentage',
  },
  methodologyEmployerOther: {
    id: 'taxIceberg.methodology.employerOther',
    defaultMessage: 'Employer unemployment insurance ({unemploymentLow}–{unemploymentHigh}, tiered by payroll size) and health insurance contribution ({healthInsurance}): Työllisyysrahasto and Verohallinto, 2026 rates.',
    description: 'Methodology bullet, values are formatted percentages',
  },
  methodologyEmployerAccident: {
    id: 'taxIceberg.methodology.employerAccident',
    defaultMessage: 'Occupational accident and group life insurance ({accidentRate} combined, industry average): Tapaturmavakuutuskeskus (TVK) annual forecast. Actual employer rates vary by industry risk class and cannot be personalized without knowing the specific employer.',
    description: 'Methodology bullet, {accidentRate} is a formatted percentage',
  },
  methodologyVat: {
    id: 'taxIceberg.methodology.vat',
    defaultMessage: 'VAT rates: Verohallinto (25.5% standard, 13.5% reduced for food/restaurants/books/medicine/transport/accommodation as of 2026). How much of your income is actually spent (versus saved) at each income level: Statistics Finland\'s Household Consumption Survey, 2022, the most recent wave, broken down by income quintile — the finest breakdown Statistics Finland publishes.',
    description: 'Methodology bullet',
  },
  methodologyVatInterpolation: {
    id: 'taxIceberg.methodology.vatInterpolation',
    defaultMessage: 'The 5 quintile data points are real survey data; the smooth curve between them is our own interpolation, not raw Statistics Finland output.',
    description: 'Methodology bullet',
  },
  methodologyDestinations: {
    id: 'taxIceberg.methodology.destinations',
    defaultMessage:
      'Where the money goes: each contribution and tax is attributed to its recipient by law (pension insurers, the Employment Fund, Kela, the municipality, the state, the parish, Yle, insurers). The municipal euro is then split by what the chosen municipality spent on each task in 2025 (net operating costs, Kuntaliitto from Valtiokonttori data; national average when no municipality is chosen), and the state euro by the 2026 state budget regrouped by purpose, with the wellbeing services counties, municipal grants, EU contributions and state pensions separated out of the Ministry of Finance class. Those splits are proportional attributions, not earmarks.',
    description: 'Methodology bullet explaining the "where the money goes" attribution',
  },
  methodologyLimitations: {
    id: 'taxIceberg.methodology.limitations',
    defaultMessage: 'This is an estimate, not tax advice: it assumes standard deductions, full-year residence, and no fringe benefits, and the VAT figure assumes typical spending patterns for your income level rather than your actual receipts.',
    description: 'Methodology limitations note',
  },
  agentsHeading: {
    id: 'taxIceberg.agents.heading',
    defaultMessage: 'For AI assistants and developers',
    description: 'Heading of the short note explaining the URL scheme, JSON API and MCP tool',
  },
  agentsBody: {
    id: 'taxIceberg.agents.body',
    defaultMessage:
      'Every salary has its own page: {urlTemplate}, with the options above as query parameters (municipality, employer, vat, church, age, children, sole, union, commuting, dividends, gains, own, assets). The same result as JSON: {apiUrl}. AI assistants can call the {tool} tool on the JobCrawls MCP connector ({connectorUrl}).',
    description: 'Note for AI assistants and developers; the placeholders are code snippets and a link',
  },
  backToSalary: {
    id: 'taxIceberg.backToSalary',
    defaultMessage: 'Back to the salary guide',
    description: 'Link back to the salary guide hub',
  },
  byline: {
    id: 'taxIceberg.byline',
    defaultMessage: 'Written by {author}',
    description: 'Author byline under the page title, {author} is a linked name',
  },
});

export default function TaxIcebergPage() {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  const locale = (pageContext?.locale ?? 'en') as string;

  // Everything the page shows is derived from the URL (salary slug + query), so a shared or
  // bookmarked link reopens exactly this view, and the server renders it for indexing.
  // The query string comes from urlOriginal: +onBeforeRoute rewrites the URL to the
  // locale-stripped logical path, and that rewrite leaves urlParsed.search empty.
  const initial = useMemo(() => {
    const original = String(pageContext?.urlOriginal ?? '');
    const search = Object.fromEntries(new URL(original, 'http://local').searchParams.entries());
    return decodeTaxIcebergState((pageContext?.routeParams as { slug?: string } | undefined)?.slug, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [grossInput, setGrossInput] = useState<number>(initial.gross);
  const [period, setPeriod] = useState<'year' | 'month'>(initial.period);
  // Gross mode: the field is the gross salary. Net mode: it is the net the person wants in hand,
  // and the gross that leaves it is solved with the current options.
  const [mode, setMode] = useState<SalaryMode>(initial.mode);
  const [taxCardRate, setTaxCardRate] = useState<number | undefined>(initial.taxCardRate);
  const [taxCardLimit, setTaxCardLimit] = useState<number | undefined>(initial.taxCardLimit);
  const [taxCardExtraRate, setTaxCardExtraRate] = useState<number | undefined>(initial.taxCardExtraRate);
  const [raiseInput, setRaiseInput] = useState<number>(initial.period === 'year' ? 6000 : 500);
  const [taxCardLimitOpen, setTaxCardLimitOpen] = useState(initial.taxCardLimit !== undefined || initial.taxCardExtraRate !== undefined);
  const [openDestination, setOpenDestination] = useState<DestinationKey | null>('state');
  const [municipality, setMunicipality] = useState<string>(initial.municipality);
  const [employerSize, setEmployerSize] = useState<EmployerSize | undefined>(initial.employerSize);
  const hasAdvancedInUrl =
    initial.employerSize !== undefined ||
    initial.churchMember ||
    initial.age !== undefined ||
    initial.dependentChildren > 0 ||
    initial.unionFees > 0 ||
    initial.commuting > 0 ||
    initial.dividends > 0 ||
    initial.capitalGains > 0 ||
    initial.unlistedDividends > 0 ||
    initial.detailedLayers;
  const [advancedOpen, setAdvancedOpen] = useState(hasAdvancedInUrl);
  // VAT on spending is the most debatable layer, so it is opt-in: off by default, one switch away.
  const [includeVat, setIncludeVat] = useState(initial.includeVat);
  // "Every tax" splits the three hidden strata into one band per tax and contribution.
  const [detailedLayers, setDetailedLayers] = useState(initial.detailedLayers);
  // Advanced options. Money amounts are kept in the selected period's units, like the salary.
  const [churchMember, setChurchMember] = useState(initial.churchMember);
  const [age, setAge] = useState<number | undefined>(initial.age);
  const [dependentChildren, setDependentChildren] = useState(initial.dependentChildren);
  const [isSoleGuardian, setIsSoleGuardian] = useState(initial.isSoleGuardian);
  const [unionFeesInput, setUnionFeesInput] = useState(initial.unionFees);
  const [commutingInput, setCommutingInput] = useState(initial.commuting);
  const [dividendsInput, setDividendsInput] = useState(initial.dividends);
  const [capitalGainsInput, setCapitalGainsInput] = useState(initial.capitalGains);
  const [unlistedDividendsInput, setUnlistedDividendsInput] = useState(initial.unlistedDividends);
  // A stock, not a flow: never converted with the Month/Year toggle. 0 means "unknown".
  const [unlistedNetAssets, setUnlistedNetAssets] = useState(initial.unlistedNetAssets);
  const [copied, setCopied] = useState(false);
  const toAnnual = (v: number) => (period === 'year' ? v : v * 12);
  const advancedInputs = {
    churchTaxRatePct: churchMember ? churchTaxRateFor(Object.prototype.hasOwnProperty.call(FINNISH_MUNICIPAL_TAX_RATES_2026, municipality) ? municipality : undefined) : undefined,
    age,
    dependentChildren,
    isSoleGuardian: dependentChildren > 0 && isSoleGuardian,
    employerSize,
    unionFees: toAnnual(unionFeesInput),
    commutingCosts: toAnnual(commutingInput),
    dividends: toAnnual(dividendsInput),
    capitalGains: toAnnual(capitalGainsInput),
    unlistedDividends: toAnnual(unlistedDividendsInput),
    unlistedNetAssets: unlistedNetAssets > 0 ? unlistedNetAssets : undefined,
    includeVat,
  };
  const advancedKey = JSON.stringify(advancedInputs);

  // FacetAutocomplete fires onChange on every keystroke, not just on selecting an option, so
  // `municipality` can briefly hold partial typed text (e.g. "H") that isn't a real municipality.
  // Only an exact match against the rate table counts as a specific selection.
  const isValidMunicipality = Object.prototype.hasOwnProperty.call(FINNISH_MUNICIPAL_TAX_RATES_2026, municipality);
  const municipalTaxRatePct = isValidMunicipality ? FINNISH_MUNICIPAL_TAX_RATES_2026[municipality] : undefined;
  const enteredAnnual = period === 'year' ? grossInput : grossInput * 12;
  // The gross everything below is computed from: entered, or solved from the target net.
  const grossAnnual = useMemo(
    () => (mode === 'net' ? solveGrossForNet(enteredAnnual, { municipalTaxRatePct, ...advancedInputs }) : enteredAnnual),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, enteredAnnual, municipalTaxRatePct, advancedKey],
  );

  const result = useMemo(() => {
    if (!(grossAnnual > 0)) return null;
    return calculateTaxIceberg({ grossAnnual, municipalTaxRatePct, ...advancedInputs });
    // advancedKey is the serialised advancedInputs object; it changes exactly when they do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossAnnual, municipalTaxRatePct, advancedKey]);

  // The salary field's pick list: the popular salaries in the selected period, each with its net,
  // computed with the same municipality and options as the main figure.
  const salaryPresets = useMemo(() => {
    const monthly = mode === 'net' ? POPULAR_MONTHLY_NETS : POPULAR_MONTHLY_SALARIES;
    return period === 'year' ? monthly.map((m) => m * 12) : monthly;
  }, [period, mode]);
  // In gross mode each preset shows its net; in net mode, the gross it takes.
  const netForPreset = useMemo(() => {
    const cache = new Map<number, number>();
    return (amount: number) => {
      const hit = cache.get(amount);
      if (hit !== undefined) return hit;
      const annual = period === 'year' ? amount : amount * 12;
      const div = period === 'year' ? 1 : 12;
      const value =
        mode === 'net'
          ? solveGrossForNet(annual, { municipalTaxRatePct, ...advancedInputs }) / div
          : calculateTaxIceberg({ grossAnnual: annual, municipalTaxRatePct, ...advancedInputs }).salary.net / div;
      cache.set(amount, value);
      return value;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, mode, municipalTaxRatePct, advancedKey]);
  const switchMode = (next: SalaryMode) => {
    if (next === mode) return;
    // Keep the same real salary across the switch: show its net, or its gross.
    const div = period === 'year' ? 1 : 12;
    if (result) setGrossInput(Math.round((next === 'net' ? result.salary.net : grossAnnual) / div));
    setMode(next);
  };

  // Raise slider: what a raise of this size leaves in hand, with the current options.
  const raise = useMemo(() => {
    if (!(grossAnnual > 0) || !(raiseInput > 0)) return null;
    const raiseAnnual = period === 'year' ? raiseInput : raiseInput * 12;
    return raiseOutcome({ grossAnnual, municipalTaxRatePct, ...advancedInputs }, raiseAnnual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossAnnual, raiseInput, period, municipalTaxRatePct, advancedKey]);
  const raiseRange = period === 'year' ? { min: 1200, max: 24000, step: 600 } : { min: 100, max: 2000, step: 50 };

  // Tax card check against the taxes this salary attracts (withholding covers taxes only).
  const taxCardCheck =
    result && taxCardRate !== undefined
      ? checkTaxCard({ grossAnnual, taxesAnnual: result.breakdown.employee.taxes.total, baseRatePct: taxCardRate, incomeLimitAnnual: taxCardLimit, extraRatePct: taxCardExtraRate })
      : null;

  // Roles whose national median is near this gross, from the salary hub (server data, embedded
  // in the HTML so the client can re-pick as the salary changes).
  const dataFromHook = useData<Data>();
  const pageData = dataFromHook ?? getTaxIcebergDataFromScript();
  const grossMonthly = grossAnnual / 12;
  const rolesNear = useMemo(() => rolesNearSalary(pageData?.roles ?? [], grossMonthly), [pageData, grossMonthly]);

  // Where every euro below the waterline goes (recipient by law, then the municipality's and the
  // state's spending profiles).
  const destinations = useMemo(
    () => (result ? taxDestinations({ result, municipality: isValidMunicipality ? municipality : '', includeVat }) : []),
    [result, isValidMunicipality, municipality, includeVat],
  );
  const destinationsTotal = destinations.reduce((sum, d) => sum + d.amount, 0);
  const municipalityName = isValidMunicipality ? municipality : formatMessage(messages.destinationsAverageMunicipality);
  const destinationCopy: Record<DestinationKey, { name: React.ReactNode; desc: React.ReactNode }> = {
    pensions: { name: <Msg {...messages.destPensions} />, desc: <Msg {...messages.destPensionsDesc} /> },
    employmentFund: { name: <Msg {...messages.destEmploymentFund} />, desc: <Msg {...messages.destEmploymentFundDesc} /> },
    kela: { name: <Msg {...messages.destKela} />, desc: <Msg {...messages.destKelaDesc} /> },
    municipality: { name: <Msg {...messages.destMunicipality} values={{ name: municipalityName }} />, desc: <Msg {...messages.destMunicipalityDesc} values={{ name: municipalityName }} /> },
    state: { name: <Msg {...messages.destState} />, desc: <Msg {...messages.destStateDesc} values={{ vat: includeVat ? formatMessage(messages.destStateVat) : '', year: TAX_YEAR }} /> },
    parish: { name: <Msg {...messages.destParish} />, desc: <Msg {...messages.destParishDesc} /> },
    yle: { name: <Msg {...messages.destYle} />, desc: <Msg {...messages.destYleDesc} /> },
    insurers: { name: <Msg {...messages.destInsurers} />, desc: <Msg {...messages.destInsurersDesc} /> },
  };
  const purposeMessages: Record<string, (typeof messages)[keyof typeof messages]> = {
    earlyChildhood: messages.muniEarlyChildhood,
    basicEducation: messages.muniBasicEducation,
    upperSecondary: messages.muniUpperSecondary,
    cultureSport: messages.muniCultureSport,
    streetsParksPlanning: messages.muniStreetsParksPlanning,
    employmentBusiness: messages.muniEmploymentBusiness,
    administrationOther: messages.muniAdministrationOther,
    healthSocialCounties: messages.stateHealthSocialCounties,
    socialSecurity: messages.stateSocialSecurity,
    municipalGrants: messages.stateMunicipalGrants,
    education: messages.stateEducation,
    defence: messages.stateDefence,
    transport: messages.stateTransport,
    employmentEconomy: messages.stateEmploymentEconomy,
    debtInterest: messages.stateDebtInterest,
    eu: messages.stateEu,
    agriculture: messages.stateAgriculture,
    securityJustice: messages.stateSecurityJustice,
    foreignAffairs: messages.stateForeignAffairs,
    environment: messages.stateEnvironment,
  };
  const purposeLabel = (key: string, group: DestinationKey) => {
    if (key === 'administrationOther' && group === 'state') return <Msg {...messages.stateAdministrationOther} />;
    const msg = purposeMessages[key];
    return msg ? <Msg {...msg} /> : key;
  };


  // Extra salaries drawn side by side with the main one (same municipality and employer size),
  // in the currently selected period's units. The scene renders at most five icebergs.
  const [compareInputs, setCompareInputs] = useState<number[]>(initial.compare);
  const compareResults = useMemo(
    () =>
      compareInputs.map((amount) => {
        const entered = period === 'year' ? amount : amount * 12;
        const annual = mode === 'net' ? solveGrossForNet(entered, { municipalTaxRatePct, ...advancedInputs }) : entered;
        return annual > 0 ? calculateTaxIceberg({ grossAnnual: annual, municipalTaxRatePct, ...advancedInputs }) : null;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compareInputs, period, mode, municipalTaxRatePct, advancedKey],
  );
  const isComparing = compareInputs.length > 0;
  // Everything but the salary itself (and its period) can be put back to the defaults in one go.
  const hasCustomOptions =
    municipality !== '' ||
    employerSize !== undefined ||
    includeVat ||
    churchMember ||
    age !== undefined ||
    dependentChildren > 0 ||
    unionFeesInput > 0 ||
    commutingInput > 0 ||
    dividendsInput > 0 ||
    capitalGainsInput > 0 ||
    unlistedDividendsInput > 0 ||
    unlistedNetAssets > 0 ||
    detailedLayers ||
    taxCardRate !== undefined ||
    compareInputs.length > 0;
  const resetOptions = () => {
    setTaxCardRate(undefined);
    setTaxCardLimit(undefined);
    setTaxCardExtraRate(undefined);
    setMunicipality('');
    setEmployerSize(undefined);
    setIncludeVat(false);
    setChurchMember(false);
    setAge(undefined);
    setDependentChildren(0);
    setIsSoleGuardian(false);
    setUnionFeesInput(0);
    setCommutingInput(0);
    setDividendsInput(0);
    setCapitalGainsInput(0);
    setUnlistedDividendsInput(0);
    setUnlistedNetAssets(0);
    setDetailedLayers(false);
    setCompareInputs([]);
  };
  const investments = result?.investments ?? null;
  // The investments iceberg takes one of the five slots when it is shown.
  const canAddComparison = compareInputs.length < MAX_ICEBERGS - 1 - (investments ? 1 : 0);

  // Keep the address bar in step with the view (replace, not push: the back button should
  // leave the page, not undo every keystroke).
  const urlState: TaxIcebergUrlState = {
    gross: grossInput,
    period,
    mode,
    taxCardRate,
    taxCardLimit,
    taxCardExtraRate,
    municipality: isValidMunicipality ? municipality : '',
    employerSize,
    includeVat,
    churchMember,
    age,
    dependentChildren,
    isSoleGuardian,
    unionFees: unionFeesInput,
    commuting: commutingInput,
    dividends: dividendsInput,
    capitalGains: capitalGainsInput,
    unlistedDividends: unlistedDividendsInput,
    unlistedNetAssets,
    detailedLayers,
    compare: compareInputs,
  };
  const currentUrl = encodeTaxIcebergUrl(urlState, locale);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname + window.location.search !== currentUrl) {
      window.history.replaceState(window.history.state, '', currentUrl);
    }
  }, [currentUrl]);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + currentUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable: the URL bar already holds the link */
    }
  };

  // Share: the OS / browser share sheet where there is one (phones, Safari, Chromium on desktop),
  // otherwise a small menu of the usual targets. The text quotes the numbers on screen, the same
  // ones the link's social card shows (see +Head.tsx), so what is sent and what unfurls agree.
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);
  const shareFacts = result ? shareFactsFromResult(intl.locale, result, grossAnnual, period === 'year') : null;
  const share = shareFacts ? shareCopy(shareFacts) : null;
  const shareNative = async () => {
    const url = window.location.origin + currentUrl;
    const data = { title: share?.title ?? document.title, text: share?.text ?? '', url };
    if (typeof navigator.share === 'function' && (typeof navigator.canShare !== 'function' || navigator.canShare(data))) {
      try {
        await navigator.share(data);
      } catch {
        /* the sheet was dismissed */
      }
      return;
    }
    setShareOpen((open) => !open);
  };
  useEffect(() => {
    if (!shareOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (shareRef.current && !shareRef.current.contains(event.target as Node)) setShareOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShareOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [shareOpen]);
  const shareTargets = () => {
    const url = window.location.origin + currentUrl;
    const title = share?.title ?? document.title;
    const text = share?.text ?? '';
    const enc = encodeURIComponent;
    return [
      { key: 'x', label: messages.shareOnX, href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`, Icon: XLogo },
      { key: 'linkedin', label: messages.shareOnLinkedIn, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, Icon: LinkedinLogo },
      { key: 'whatsapp', label: messages.shareOnWhatsApp, href: `https://wa.me/?text=${enc(`${title} ${url}`)}`, Icon: WhatsappLogo },
      { key: 'email', label: messages.shareByEmail, href: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}`, Icon: EnvelopeSimple },
    ];
  };

  const layerLabels = {
    employerContributions: formatMessage(messages.layerEmployerContributions),
    employeeTax: formatMessage(messages.layerEmployeeTax),
    vat: formatMessage(messages.layerVat),
    netAfterVat: formatMessage(messages.layerNetAfterVat),
  };

  const fmtCurrency = (amount: number) =>
    intl.formatNumber(amount, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  // The calculators work in annual euros; everything the reader sees follows the Month/Year
  // toggle, so a monthly picture shows monthly amounts on the ice, in the cards, and in the table.
  const perPeriod = (annual: number) => (period === 'month' ? annual / 12 : annual);
  const fmtAmount = (annual: number) => fmtCurrency(perPeriod(annual));
  const fmtPct = (pct: number) => intl.formatNumber(pct / 100, { style: 'percent', maximumFractionDigits: 1 });
  const copyLocale = asCopyLocale(locale);
  const slug = (pageContext?.routeParams as { slug?: string } | undefined)?.slug;
  const heading = salaryPageCopy(copyLocale, slug)?.h1 ?? pageCopy(copyLocale).h1;
  const words = glossary(copyLocale);
  const taxCard = taxCardSection(copyLocale);

  const fmtSigned = (annual: number) =>
    intl.formatNumber(perPeriod(annual), { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, signDisplay: 'exceptZero' });
  const fmtShare = (fraction: number) => formatMessage(messages.detailShare, { share: fmtPct(fraction * 100) });

  const icebergTitle = (grossInPeriod: number) =>
    formatMessage(period === 'year' ? messages.icebergTitleYear : messages.icebergTitleMonth, { amount: fmtCurrency(grossInPeriod) });

  /** What each layer of an iceberg is made of, for the hover/tap detail cards. */
  const layerDetails = (r: TaxIcebergResult): IcebergSpec['details'] => {
    const { employer, employee, vat } = r.breakdown;
    const municipalRate = municipalTaxRatePct ?? TAX_PARAMS_2026.avgMunicipalRatePct;
    const employeeLines = [
      formatMessage(messages.detailEmployeeState, { amount: fmtAmount(employee.taxes.state) }),
      formatMessage(messages.detailEmployeeMunicipal, { rate: fmtPct(municipalRate), amount: fmtAmount(employee.taxes.municipal) }),
      formatMessage(messages.detailEmployeeMedical, { amount: fmtAmount(employee.taxes.medicalCare) }),
      formatMessage(messages.detailEmployeeYle, { amount: fmtAmount(employee.taxes.yle) }),
      ...(employee.taxes.church > 0 ? [formatMessage(messages.detailEmployeeChurch, { amount: fmtAmount(employee.taxes.church) })] : []),
      formatMessage(messages.detailEmployeePension, { amount: fmtAmount(employee.contributions.pension) }),
      formatMessage(messages.detailEmployeeUnemployment, { amount: fmtAmount(employee.contributions.unemployment) }),
      ...(employee.contributions.dailyAllowance > 0
        ? [formatMessage(messages.detailEmployeeDailyAllowance, { amount: fmtAmount(employee.contributions.dailyAllowance) })]
        : []),
      ...(employee.earnedIncomeCredit > 0 ? [formatMessage(messages.detailEmployeeCredit, { amount: fmtSigned(-employee.earnedIncomeCredit) })] : []),
      ...(employee.otherDeductions.unionFees > 0
        ? [formatMessage(messages.detailEmployeeUnionDeducted, { amount: fmtAmount(employee.otherDeductions.unionFees) })]
        : []),
      ...(employee.otherDeductions.commuting > 0
        ? [formatMessage(messages.detailEmployeeCommutingDeducted, { amount: fmtAmount(employee.otherDeductions.commuting) })]
        : []),
    ];
    return {
      employerContributions: [
        formatMessage(messages.detailEmployerTyel, { rate: fmtPct(EMPLOYER_COST_PARAMS_2026.tyelEmployerRate * 100), amount: fmtAmount(employer.tyel) }),
        formatMessage(messages.detailEmployerUnemployment, { amount: fmtAmount(employer.unemploymentInsurance) }),
        formatMessage(messages.detailEmployerHealth, { amount: fmtAmount(employer.healthInsurance) }),
        formatMessage(messages.detailEmployerAccident, { amount: fmtAmount(employer.accidentInsurance + employer.groupLife) }),
      ],
      employeeTax: employeeLines,
      vat: [
        formatMessage(messages.detailVatAssumption, { share: fmtPct(vat.vatLiableSharePct), rate: fmtPct(vat.effectiveVatRatePct) }),
        formatMessage(messages.detailVatBasis),
      ],
      netAfterVat: [
        formatMessage(messages.detailNetPayslip, { amount: fmtAmount(r.salary.net) }),
        ...(includeVat ? [formatMessage(messages.detailNetMinusVat, { amount: fmtAmount(r.salary.vat) })] : []),
        formatMessage(messages.detailNetShareOfGross, { share: fmtPct((r.salary.netAfterVat / r.salary.grossAnnual) * 100) }),
      ],
    };
  };

  /** Detail lines for the investments iceberg. */
  const investmentDetails = (r: TaxIcebergResult): IcebergSpec['details'] => {
    const inv = r.investments;
    if (!inv) return {};
    const c = r.breakdown.capital;
    const taxLines = [
      ...(c.dividends > 0 ? [formatMessage(messages.detailInvestmentsListed, { amount: fmtAmount(c.dividends) })] : []),
      ...(c.capitalGains > 0 ? [formatMessage(messages.detailInvestmentsGains, { amount: fmtAmount(c.capitalGains) })] : []),
      ...(c.unlistedDividends > 0 ? [formatMessage(messages.detailInvestmentsOwn, { amount: fmtAmount(c.unlistedDividends) })] : []),
      ...(c.unlistedDividends > 0
        ? [
            c.unlisted.heavy > 0
              ? formatMessage(messages.detailCapitalUnlistedSplit, { light: fmtAmount(c.unlisted.light), heavy: fmtAmount(c.unlisted.heavy) })
              : formatMessage(messages.detailCapitalUnlistedLight, { light: fmtAmount(c.unlisted.light) }),
          ]
        : []),
      formatMessage(messages.detailEmployeeCapitalTax, { amount: fmtAmount(inv.capitalTax) }),
      ...(inv.earnedIncomeTax > 0 ? [formatMessage(messages.detailEmployeeUnlistedEarned, { amount: fmtAmount(inv.earnedIncomeTax) })] : []),
    ];
    return {
      investmentTax: taxLines,
      capitalTax: taxLines,
      investmentEarnedTax: [formatMessage(messages.detailEmployeeUnlistedEarned, { amount: fmtAmount(inv.earnedIncomeTax) })],
      netAfterVat: [
        formatMessage(messages.detailInvestmentsGross, { amount: fmtAmount(inv.gross) }),
        formatMessage(messages.detailInvestmentsMinusTax, { amount: fmtAmount(inv.tax) }),
        ...(includeVat ? [formatMessage(messages.detailNetMinusVat, { amount: fmtAmount(inv.vat) })] : []),
      ],
    };
  };

  /** Whole-iceberg summary for the caption in comparison mode; `first` is the main salary. */
  const icebergSummary = (r: TaxIcebergResult, first: TaxIcebergResult | null): string[] => [
    formatMessage(messages.summaryGross, { amount: fmtAmount(r.salary.grossAnnual) }),
    formatMessage(messages.summaryTrueCost, { amount: fmtAmount(r.salary.trueEmployerCost) }),
    formatMessage(messages.summaryKeep, { amount: fmtAmount(r.salary.netAfterVat), rate: fmtPct(r.salary.keepRatePct) }),
    ...(first && first !== r
      ? [
          formatMessage(messages.summaryDelta, {
            deltaGross: fmtSigned(r.salary.grossAnnual - first.salary.grossAnnual),
            deltaKept: fmtSigned(r.salary.netAfterVat - first.salary.netAfterVat),
          }),
        ]
      : []),
  ];
  const investmentsSummary = (r: TaxIcebergResult): string[] => {
    const inv = r.investments;
    if (!inv) return [];
    return [
      formatMessage(messages.summaryCapital, { amount: fmtAmount(inv.gross) }),
      formatMessage(messages.summaryInvestmentsTax, { amount: fmtAmount(inv.tax) }),
      formatMessage(messages.summaryInvestmentsKeep, { amount: fmtAmount(inv.netAfterVat), rate: fmtPct(inv.keepRatePct) }),
    ];
  };

  /** The strata of one iceberg, bottom (keel) to top (tip). Amounts are annual here; the caller
   * converts to the selected period. */
  const buildLayers = (r: TaxIcebergResult): IcebergLayer[] => {
    const tip: IcebergLayer = { key: 'netAfterVat', amount: r.salary.netAfterVat, label: layerLabels.netAfterVat, color: '#f6fbfd', textColor: '#12324a' };
    if (!detailedLayers) {
      return [
        { key: 'employerContributions', amount: r.salary.employerContributionsTotal, label: layerLabels.employerContributions, color: '#123f63' },
        { key: 'employeeTax', amount: r.salary.employeeTax, label: layerLabels.employeeTax, color: '#1c6493' },
        ...(includeVat ? [{ key: 'vat', amount: r.salary.vat, label: layerLabels.vat, color: '#3a92c2' }] : []),
        tip,
      ];
    }
    const { employer, employee } = r.breakdown;
    const layers: IcebergLayer[] = [
      // employer side, deepest first: the navy family
      { key: 'employerAccident', amount: employer.accidentInsurance + employer.groupLife, label: formatMessage(messages.layerEmployerAccident), color: '#0d2f4c' },
      { key: 'employerHealth', amount: employer.healthInsurance, label: formatMessage(messages.layerEmployerHealth), color: '#113a5c' },
      { key: 'employerUnemployment', amount: employer.unemploymentInsurance, label: formatMessage(messages.layerEmployerUnemployment), color: '#15466d' },
      { key: 'employerTyel', amount: employer.tyel, label: formatMessage(messages.layerEmployerTyel), color: '#1a527e' },
      // the employee's own contributions and taxes: the mid-blue family
      { key: 'employeeUnemployment', amount: employee.contributions.unemployment, label: formatMessage(messages.layerEmployeeUnemployment), color: '#1f5e8d' },
      { key: 'employeePension', amount: employee.contributions.pension, label: formatMessage(messages.layerEmployeePension), color: '#23699b' },
      { key: 'employeeDailyAllowance', amount: employee.contributions.dailyAllowance, label: formatMessage(messages.layerEmployeeDailyAllowance), color: '#2773a8' },
      { key: 'yle', amount: employee.taxes.yle, label: formatMessage(messages.layerYle), color: '#2b7db4' },
      { key: 'church', amount: employee.taxes.church, label: formatMessage(messages.layerChurch), color: '#2f86bf' },
      { key: 'medicalCare', amount: employee.taxes.medicalCare, label: formatMessage(messages.layerMedicalCare), color: '#348fc8' },
      { key: 'municipalTax', amount: employee.taxes.municipal, label: formatMessage(messages.layerMunicipalTax), color: '#3a98d0' },
      { key: 'stateTax', amount: employee.taxes.state, label: formatMessage(messages.layerStateTax), color: '#42a1d7' },
      { key: 'vat', amount: r.salary.vat, label: layerLabels.vat, color: '#66b8e2' },
      tip,
    ];
    // Drop strata that do not apply (no church tax, no capital income, under the daily-allowance
    // threshold) rather than drawing zero-height bands with legend entries.
    return layers.filter((l) => l.amount > 0 || l === tip);
  };

  /** The investments iceberg: no employer side, just the tax on the income and what is left. */
  const buildInvestmentLayers = (r: TaxIcebergResult): IcebergLayer[] => {
    const inv = r.investments;
    if (!inv) return [];
    const tip: IcebergLayer = { key: 'netAfterVat', amount: inv.netAfterVat, label: layerLabels.netAfterVat, color: '#f6fbfd', textColor: '#12324a' };
    const vat: IcebergLayer[] = includeVat ? [{ key: 'vat', amount: inv.vat, label: layerLabels.vat, color: '#66b8e2' }] : [];
    const layers: IcebergLayer[] = detailedLayers
      ? [
          { key: 'investmentEarnedTax', amount: inv.earnedIncomeTax, label: formatMessage(messages.layerInvestmentEarnedTax), color: '#1f5e8d' },
          { key: 'capitalTax', amount: inv.capitalTax, label: formatMessage(messages.layerCapitalTax), color: '#4faadc' },
          ...vat,
          tip,
        ]
      : [{ key: 'investmentTax', amount: inv.tax, label: formatMessage(messages.layerInvestmentTax), color: '#1c6493' }, ...vat, tip];
    return layers.filter((l) => l.amount > 0 || l === tip);
  };

  const icebergSpecs: IcebergSpec[] = result
    ? [
        {
          id: 'main',
          layers: buildLayers(result).map((l) => ({ ...l, amount: perPeriod(l.amount) })),
          title: isComparing || investments ? icebergTitle(grossInput) : undefined,
          details: layerDetails(result),
          summary: isComparing || investments ? icebergSummary(result, null) : undefined,
        },
        ...compareResults.flatMap((r, i) =>
          r
            ? [
                {
                  id: `compare-${i}`,
                  layers: buildLayers(r).map((l) => ({ ...l, amount: perPeriod(l.amount) })),
                  title: icebergTitle(compareInputs[i]),
                  details: layerDetails(r),
                  summary: icebergSummary(r, result),
                },
              ]
            : [],
        ),
        ...(investments
          ? [
              {
                id: 'investments',
                layers: buildInvestmentLayers(result).map((l) => ({ ...l, amount: perPeriod(l.amount) })),
                title: formatMessage(messages.investmentsTitle),
                details: investmentDetails(result),
                summary: investmentsSummary(result),
                formatShare: (fraction: number) => formatMessage(messages.detailShareInvestments, { share: fmtPct(fraction * 100) }),
              },
            ]
          : []),
      ]
    : [];

  const estimateNote =
    !isValidMunicipality || employerSize === undefined ? (
      <Msg
        {...(!isValidMunicipality && employerSize === undefined
          ? messages.genericEstimateNoteBoth
          : !isValidMunicipality
            ? messages.genericEstimateNoteMunicipal
            : messages.genericEstimateNoteEmployerSize)}
        values={{ rate: fmtPct(TAX_PARAMS_2026.avgMunicipalRatePct) }}
      />
    ) : null;

  const labelClass = 'mb-1 block text-[11px] font-bold uppercase tracking-wide text-jc-muted';

  /* The salary control (mode tabs, field, period toggle) renders twice: alone above the picture on
     phones, and as the first toolbar cell from md up. One copy is always display:none, so the ids
     differ but only one is ever in the accessibility tree. */
  const renderSalaryControl = (id: string) => (
    <>
      <div role="group" aria-label={formatMessage(messages.salaryModeLabel)} className="mb-0.5 flex items-center gap-3">
        {(['gross', 'net'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            aria-pressed={mode === m}
            className={`text-[11px] font-bold uppercase tracking-wide transition-colors ${
              mode === m ? 'text-[#1c6493] underline decoration-2 underline-offset-4 dark:text-[#cfe6f3]' : 'text-[#7fa6bd] hover:text-[#1c6493] dark:text-[#6f9cb8]'
            }`}
          >
            <Msg {...(m === 'net' ? messages.salaryModeNet : messages.salaryInputLabel)} />
          </button>
        ))}
      </div>
      <SalaryCombobox
        id={id}
        value={grossInput}
        onChange={setGrossInput}
        presets={salaryPresets}
        netFor={netForPreset}
        formatCurrency={fmtCurrency}
        presetsLabel={formatMessage(messages.salaryPresetsLabel)}
        netText={(value) => formatMessage(mode === 'net' ? messages.salaryPresetGross : messages.salaryPresetNet, { amount: fmtCurrency(value) })}
      >
          <div className="flex shrink-0 items-center gap-0.5 rounded-full bg-[#e3f2f9] p-0.5 dark:bg-[#123a55]">
            {(['month', 'year'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  if (p === period) return;
                  // Convert every entered amount so it keeps representing the same real
                  // money across the toggle; otherwise the same digits would mean a wildly
                  // different (and always-annual) result depending on which button is active.
                  const convert = (v: number) => Math.round(p === 'year' ? v * 12 : v / 12);
                  setGrossInput(convert);
                  setCompareInputs((prev) => prev.map(convert));
                  setUnionFeesInput(convert);
                  setCommutingInput(convert);
                  setDividendsInput(convert);
                  setCapitalGainsInput(convert);
                  setUnlistedDividendsInput(convert);
                  setRaiseInput(convert);
                  setPeriod(p);
                }}
                aria-pressed={period === p}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                  period === p ? 'bg-[#1c6493] text-white shadow-sm' : 'text-[#4d7d99] hover:text-[#1c6493] dark:text-[#8fc0dd]'
                }`}
              >
                <Msg {...(p === 'year' ? messages.periodYear : messages.periodMonth)} />
              </button>
            ))}
          </div>
      </SalaryCombobox>
    </>
  );

  return (
    <div className="min-h-screen bg-jc-page">
      {pageData && <script id={TAX_ICEBERG_PAGE_SCRIPT_ID} type="application/json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageData).replace(/</g, '\\u003c') }} />}
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 sm:py-4">
        {/* Breadcrumb on the left, the link actions (copy, share) on the right, one row. */}
        <div className="mb-1.5 flex items-start justify-between gap-3">
          <BreadcrumbNavigation
            items={[
              { label: formatMessage(messages.breadcrumbParent), href: '/salary' },
              { label: formatMessage(messages.breadcrumbCurrent) },
            ]}
            className="min-w-0 px-0 sm:px-0"
          />
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={copyLink} className={`${ui.dashedButton} px-2.5 py-1 text-xs`} aria-live="polite">
              {copied ? <Check className="h-3.5 w-3.5" /> : <LinkSimple className="h-3.5 w-3.5" />}
              <Msg {...(copied ? messages.copied : messages.copyLink)} />
            </button>
            <div ref={shareRef} className="relative">
              <button type="button" onClick={shareNative} className={`${ui.dashedButton} px-2.5 py-1 text-xs`} aria-haspopup="menu" aria-expanded={shareOpen}>
                <ShareNetwork className="h-3.5 w-3.5" />
                <Msg {...messages.share} />
              </button>
              {shareOpen && (
                <div
                  role="menu"
                  aria-label={formatMessage(messages.shareMenuLabel)}
                  className="absolute right-0 z-20 mt-1 w-56 rounded-xl border-2 border-[#bfe0f0] bg-white p-1 shadow-lg dark:border-[#2a5b80] dark:bg-[#0b1f30]"
                >
                  {shareTargets().map(({ key, label, href, Icon }) => (
                    <a key={key} role="menuitem" href={href} target="_blank" rel="noopener noreferrer" className={ui.menuItem} onClick={() => setShareOpen(false)}>
                      <Icon className="h-4 w-4" />
                      <Msg {...label} />
                    </a>
                  ))}
                  <button
                    type="button"
                    role="menuitem"
                    className={ui.menuItem}
                    onClick={() => {
                      void copyLink();
                      setShareOpen(false);
                    }}
                  >
                    <LinkSimple className="h-4 w-4" />
                    <Msg {...messages.copyLink} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compact header: title and one-line lede, byline tucked to the right on wide screens. */}
        <header className="mb-3 flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
          <div className="min-w-0 max-w-3xl lg:max-w-none">
            <h1 className="font-display text-xl font-bold leading-tight text-jc-ink sm:text-2xl">{heading}</h1>
            <p className="mt-1 text-sm leading-snug text-jc-muted">
              <Msg {...messages.intro} />
            </p>
          </div>
          <p className="shrink-0 text-xs text-jc-muted">
            <Msg
              {...messages.byline}
              values={{
                author: (
                  <a href="/authors/ashton-spina" className="font-semibold text-jc-ink underline-offset-2 hover:underline">
                    {ASHTON_ARTICLE_AUTHOR.name}
                  </a>
                ),
              }}
            />
          </p>
        </header>

        {/* Below md the order is: salary field, the picture, then the rest of the controls, so the
            iceberg is on the first screen of a phone instead of under four stacked inputs. From md
            up the toolbar moves back above the picture (order-last vs md:order-first), in CSS so server and
            client markup match. */}
        <div className="mb-4 flex flex-col gap-3">
          <div className={`md:hidden ${ui.card}`}>{renderSalaryControl('tax-iceberg-salary-m')}</div>

        {/* Toolbar: the everyday inputs in one row on wide screens (two columns on tablets,
            stacked on phones). Every control is the same 44px tall with its label above, so the
            row reads as one line; niche inputs sit behind the Advanced options disclosure. The
            whole block is kept short on purpose so the iceberg scene lands above the fold. */}
        <div className={`order-last md:order-first ${ui.card}`}>
          <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] lg:items-end">
            <div className="hidden min-w-0 md:block">{renderSalaryControl('tax-iceberg-salary')}</div>

            <div className="min-w-0" title={formatMessage(messages.municipalityLabel)}>
              <label htmlFor="tax-iceberg-municipality" className={ui.label}>
                <Msg {...messages.toolbarMunicipality} />
              </label>
              <MunicipalityCombobox
                id="tax-iceberg-municipality"
                value={municipality}
                onChange={setMunicipality}
                placeholder={formatMessage(messages.municipalityPlaceholder)}
                clearLabel={formatMessage(messages.municipalityPlaceholder)}
              />
            </div>

            <div className="min-w-0">
              <span className={ui.label} id="tax-iceberg-vat-label">
                <Msg {...messages.vatFieldLabel} />
              </span>
              <button
                type="button"
                role="switch"
                aria-labelledby="tax-iceberg-vat-label"
                aria-checked={includeVat}
                onClick={() => setIncludeVat((v) => !v)}
                className={`flex h-11 w-full items-center gap-3 rounded-xl border-2 px-3 text-left transition-colors ${
                  includeVat
                    ? 'border-[#3a92c2] bg-[#e3f2f9] dark:border-[#5fa9d4] dark:bg-[#123a55]'
                    : 'border-[#bfe0f0] bg-[#f3fafd] hover:border-[#9ccfe8] dark:border-[#2a5b80] dark:bg-[#0f2a40]'
                }`}
              >
                <span className={`relative inline-block h-6 w-11 shrink-0 rounded-full transition-colors ${includeVat ? 'bg-[#1c6493]' : 'bg-[#bfe0f0] dark:bg-[#2a5b80]'}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${includeVat ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </span>
                <span>
                  <span className="block text-sm font-bold leading-tight text-[#1c6493] dark:text-[#cfe6f3]">
                    <Msg {...messages.vatToggleLabel} />
                  </span>
                  <span className="block text-[11px] leading-tight text-[#4d7d99] dark:text-[#8fc0dd]">
                    <Msg {...messages.vatToggleHint} />
                  </span>
                </span>
              </button>
            </div>

            {canAddComparison && (
              <div className="min-w-0 lg:min-w-[220px]">
                {/* Blank label keeps the button on the same baseline as the fields in the row. */}
                <span className={`${ui.label} hidden lg:block`} aria-hidden="true">
                  &nbsp;
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setCompareInputs((prev) => {
                      const last = prev.length ? prev[prev.length - 1] : grossInput;
                      // Seed the new field with a noticeably different salary so the two
                      // icebergs differ at a glance instead of drawing twins.
                      const bump = period === 'year' ? 12000 : 1000;
                      return [...prev, Math.max(0, Math.round(last + bump))];
                    })
                  }
                  className={`${ui.dashedButton} h-11 w-full justify-center whitespace-nowrap`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <Msg {...messages.compareAdd} />
                </button>
              </div>
            )}
          </div>
            {compareInputs.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {compareInputs.map((value, i) => (
                  <div key={i} className={`${ui.field} py-1 pl-2.5 pr-1`}>
                    <span className={`${ui.prefix} text-sm`}>€</span>
                    <input
                      type="number"
                      min={0}
                      step={100}
                      value={value}
                      aria-label={formatMessage(messages.salaryInputLabel)}
                      onChange={(e) => {
                        const next = Math.max(0, Number(e.target.value) || 0);
                        setCompareInputs((prev) => prev.map((v, j) => (j === i ? next : v)));
                      }}
                      className={`${ui.input} w-20 text-sm font-bold`}
                    />
                    <button
                      type="button"
                      onClick={() => setCompareInputs((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={formatMessage(messages.compareRemove)}
                      className={ui.iconButton}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          {/* One footer line: the freshness note on the left, reset and the Advanced options
              disclosure on the right. The open panel wraps onto its own full-width row. */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <p className="min-w-0 flex-1 basis-[18rem] text-[11px] leading-snug text-[#4d7d99] dark:text-[#8fc0dd]">
              <span className="font-semibold">
                <Msg {...messages.taxYearNote} values={{ year: TAX_YEAR }} />
              </span>
              {' '}
              {words.gross}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {hasCustomOptions && (
                <button type="button" onClick={resetOptions} className={`${ui.dashedButton} px-2.5 py-1 text-xs`}>
                  <ArrowCounterClockwise className="h-3.5 w-3.5" />
                  <Msg {...messages.resetOptions} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                aria-expanded={advancedOpen}
                aria-controls="tax-iceberg-advanced"
                className="flex items-center gap-1.5 rounded-lg px-1 py-1 text-left text-xs font-bold uppercase tracking-wide text-[#4d7d99] transition-colors hover:text-[#1c6493] dark:text-[#8fc0dd]"
              >
                <span>
                  <Msg {...messages.advancedToggle} />
                </span>
                <CaretDown className={`h-4 w-4 shrink-0 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {advancedOpen && (
              <div id="tax-iceberg-advanced" className="grid basis-full gap-x-6 gap-y-5 border-t border-[#d7ecf5] px-1 pb-1 pt-3 md:grid-cols-2 xl:grid-cols-4 dark:border-[#24506f]">
                <section className="flex flex-col gap-3">
                  <h3 className={ui.groupTitle}>
                    <Msg {...messages.advancedGroupEmployer} />
                  </h3>
                  <Field label={formatMessage(messages.employerSizeShort)} hint={formatMessage(messages.employerSizeHint)}>
                    <Segmented<EmployerSize | undefined>
                      value={employerSize}
                      onChange={setEmployerSize}
                      options={[
                        { value: undefined, label: formatMessage(messages.employerSizeUnknown) },
                        { value: 'small', label: formatMessage(messages.employerSizeSmall) },
                        { value: 'large', label: formatMessage(messages.employerSizeLarge) },
                      ]}
                    />
                  </Field>
                  <h3 className={`${ui.groupTitle} mt-1`}>
                    <Msg {...messages.advancedGroupPicture} />
                  </h3>
                  <Field label={formatMessage(messages.detailToggleLabel)} hint={formatMessage(messages.detailToggleHint)}>
                    <Segmented<boolean>
                      value={detailedLayers}
                      onChange={setDetailedLayers}
                      options={[
                        { value: false, label: formatMessage(messages.detailSimple) },
                        { value: true, label: formatMessage(messages.detailFull) },
                      ]}
                    />
                  </Field>
                </section>

                <section className="flex flex-col gap-3">
                  <h3 className={ui.groupTitle}>
                    <Msg {...messages.advancedGroupPerson} />
                  </h3>
                  <Field label={formatMessage(messages.churchShort)} hint={formatMessage(messages.churchHint, { rate: fmtPct(CHURCH_TAX_AVG_RATE_PCT) })}>
                    <Segmented<boolean>
                      value={churchMember}
                      onChange={setChurchMember}
                      options={[
                        { value: false, label: formatMessage(messages.optionNo) },
                        { value: true, label: formatMessage(messages.optionYes) },
                      ]}
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field id="tax-iceberg-age" label={formatMessage(messages.ageShort)} hint={formatMessage(messages.ageHint)}>
                      <div className={`${ui.field} h-11`}>
                        <input
                          id="tax-iceberg-age"
                          type="number"
                          min={15}
                          max={100}
                          value={age ?? ''}
                          placeholder={formatMessage(messages.agePlaceholder)}
                          onChange={(e) => setAge(e.target.value === '' ? undefined : Math.max(0, Math.min(120, Number(e.target.value) || 0)))}
                          className={`${ui.input} text-sm font-bold`}
                        />
                      </div>
                    </Field>
                    <Field id="tax-iceberg-children" label={formatMessage(messages.childrenShort)} hint={formatMessage(messages.childrenHint)}>
                      <div className={`${ui.field} h-11`}>
                        <input
                          id="tax-iceberg-children"
                          type="number"
                          min={0}
                          max={20}
                          value={dependentChildren}
                          onChange={(e) => setDependentChildren(Math.max(0, Math.min(20, Math.round(Number(e.target.value) || 0))))}
                          className={`${ui.input} text-sm font-bold`}
                        />
                      </div>
                    </Field>
                  </div>
                  {dependentChildren > 0 && (
                    <Field label={formatMessage(messages.soleGuardianLabel)} hint={formatMessage(messages.soleGuardianHint)}>
                      <Segmented<boolean>
                        value={isSoleGuardian}
                        onChange={setIsSoleGuardian}
                        options={[
                          { value: false, label: formatMessage(messages.optionNo) },
                          { value: true, label: formatMessage(messages.optionYes) },
                        ]}
                      />
                    </Field>
                  )}
                </section>

                <section className="flex flex-col gap-3">
                  <h3 className={ui.groupTitle}>
                    <Msg {...messages.advancedGroupDeductions} />
                  </h3>
                  <MoneyField
                    id="tax-iceberg-union-fees"
                    label={formatMessage(messages.unionFeesLabel)}
                    hint={formatMessage(messages.unionFeesHint)}
                    value={unionFeesInput}
                    onChange={setUnionFeesInput}
                    suffix={formatMessage(period === 'month' ? messages.unitSuffixMonth : messages.unitSuffixYear)}
                  />
                  <MoneyField
                    id="tax-iceberg-commuting"
                    label={formatMessage(messages.commutingShort)}
                    hint={formatMessage(messages.commutingHint, {
                      ownLiability: fmtCurrency(TAX_PARAMS_2026.commutingOwnLiability),
                      max: fmtCurrency(TAX_PARAMS_2026.commutingDeductionMax),
                    })}
                    value={commutingInput}
                    onChange={setCommutingInput}
                    suffix={formatMessage(period === 'month' ? messages.unitSuffixMonth : messages.unitSuffixYear)}
                  />
                </section>

                <section className="flex flex-col gap-3">
                  <h3 className={ui.groupTitle}>
                    <Msg {...messages.advancedGroupOtherIncome} />
                  </h3>
                  <MoneyField
                    id="tax-iceberg-dividends"
                    label={formatMessage(messages.dividendsLabel)}
                    hint={formatMessage(messages.dividendsHint)}
                    value={dividendsInput}
                    onChange={setDividendsInput}
                    suffix={formatMessage(period === 'month' ? messages.unitSuffixMonth : messages.unitSuffixYear)}
                  />
                  <MoneyField
                    id="tax-iceberg-capital-gains"
                    label={formatMessage(messages.capitalGainsLabel)}
                    hint={formatMessage(messages.capitalGainsHint)}
                    value={capitalGainsInput}
                    onChange={setCapitalGainsInput}
                    suffix={formatMessage(period === 'month' ? messages.unitSuffixMonth : messages.unitSuffixYear)}
                  />
                  <MoneyField
                    id="tax-iceberg-unlisted-dividends"
                    label={formatMessage(messages.unlistedDividendsLabel)}
                    hint={formatMessage(messages.unlistedDividendsHint, { cap: fmtCurrency(CAPITAL_INCOME_PARAMS_2026.unlistedLightCap) })}
                    value={unlistedDividendsInput}
                    onChange={setUnlistedDividendsInput}
                    suffix={formatMessage(period === 'month' ? messages.unitSuffixMonth : messages.unitSuffixYear)}
                  />
                  <MoneyField
                    id="tax-iceberg-net-assets"
                    label={formatMessage(messages.netAssetsLabel)}
                    hint={formatMessage(messages.netAssetsHint)}
                    value={unlistedNetAssets}
                    onChange={setUnlistedNetAssets}
                    suffix=""
                  />
                  <p className="text-[11px] leading-relaxed text-[#4d7d99] dark:text-[#8fc0dd]">
                    <Msg
                      {...messages.otherIncomeHint}
                      values={{
                        lower: fmtPct(CAPITAL_INCOME_PARAMS_2026.lowerRate * 100),
                        upper: fmtPct(CAPITAL_INCOME_PARAMS_2026.upperRate * 100),
                        threshold: fmtCurrency(CAPITAL_INCOME_PARAMS_2026.upperRateThreshold),
                      }}
                    />
                  </p>
                </section>
              </div>
            )}
          </div>
        </div>

        {/* Hero: the full-width panorama, with the headline stat floating over the sky from xl up
            (where the scene is wide enough that the card only covers sky) and stacked under the
            picture on smaller screens and in comparison mode. */}
        {result && (
          <div className="relative">
            {/* No padding: the scene runs to the card's edges and its rounded corners clip it;
                the legend inside the visual keeps its own bezel. */}
            <JcCard className="overflow-hidden p-0">
              <IcebergVisual
                icebergs={icebergSpecs}
                formatCurrency={fmtCurrency}
                formatShare={fmtShare}
                ariaLabel={formatMessage(messages.visualAriaLabel)}
                unitLabel={formatMessage(period === 'month' ? messages.amountsPerMonth : messages.amountsPerYear)}
              />
            </JcCard>
            <div
              className={`mt-3 rounded-2xl border border-jc-card-border bg-jc-surface/95 px-4 py-3 shadow-sm backdrop-blur-sm ${
                isComparing || investments ? '' : 'xl:pointer-events-none xl:absolute xl:left-6 xl:top-6 xl:mt-0 xl:max-w-xs xl:px-5 xl:py-4'
              }`}
            >
              <p className="font-display text-base font-bold leading-snug text-jc-ink xl:text-lg">
                <Msg
                  {...(includeVat ? messages.headlineStat : messages.headlineStatNoVat)}
                  values={{ trueEmployerCost: fmtAmount(result.salary.trueEmployerCost), netAfterVat: fmtAmount(result.salary.netAfterVat) }}
                />
              </p>
              {mode === 'net' && (
                <p className="mt-1 text-sm font-semibold text-jc-ink">
                  <Msg {...messages.netModeStat} values={{ net: fmtAmount(result.salary.net), gross: fmtAmount(grossAnnual) }} />
                </p>
              )}
              <p className="mt-1 text-sm text-jc-muted">
                <Msg {...messages.keepRateStat} values={{ rate: fmtPct(result.salary.keepRatePct) }} />
              </p>
              <p className="mt-1 text-sm text-jc-muted">
                <Msg
                  {...messages.taxRateStat}
                  values={{
                    rate: fmtPct((result.breakdown.employee.taxes.total / result.salary.grossAnnual) * 100),
                    total: fmtPct(result.breakdown.employee.effectiveRatePct),
                  }}
                />
              </p>
              <p className="mt-2 text-xs text-jc-muted">{words.net}</p>
              {investments && (
                <p className="mt-2 text-sm font-semibold text-jc-ink">
                  <Msg {...messages.investmentsStat} values={{ gross: fmtAmount(investments.gross), net: fmtAmount(investments.netAfterVat) }} />
                </p>
              )}
              {(isComparing || investments) && (
                <p className="mt-2 text-xs italic text-jc-muted">
                  <Msg {...messages.compareHint} />
                </p>
              )}
              {estimateNote && <p className="mt-2 text-xs italic text-jc-muted">{estimateNote}</p>}
            </div>
          </div>
        )}
        </div>

        {result && (
          <div className="mb-4 grid items-start gap-4 lg:grid-cols-2">
            {/* A raise, and what survives of it. */}
            <JcCard className="p-4 sm:p-5">
              <h2 className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted sm:text-sm">
                <Msg {...messages.raiseHeading} />
              </h2>
              <div className="mt-3 flex items-center gap-3">
                <input
                  type="range"
                  min={raiseRange.min}
                  max={raiseRange.max}
                  step={raiseRange.step}
                  value={Math.min(raiseRange.max, Math.max(raiseRange.min, raiseInput))}
                  onChange={(e) => setRaiseInput(Number(e.target.value))}
                  aria-label={formatMessage(messages.raiseLabel)}
                  className="w-full accent-[#1c6493]"
                />
                <span className="w-24 shrink-0 text-right font-display text-lg font-bold text-jc-ink">+{fmtCurrency(raiseInput)}</span>
              </div>
              {raise && (
                <>
                  <p className="mt-2 text-sm text-jc-ink">
                    <Msg
                      {...messages.raiseResult}
                      values={{
                        raise: fmtCurrency(raiseInput),
                        gain: fmtAmount(raise.netGain),
                        kept: fmtPct(raise.keptPct),
                        period: formatMessage(period === 'year' ? messages.perYearWord : messages.perMonthWord),
                      }}
                    />
                  </p>
                  <p className="mt-1 text-xs text-jc-muted">
                    <Msg {...messages.raiseEmployer} values={{ cost: fmtAmount(raise.employerCostIncrease) }} />
                  </p>
                </>
              )}
            </JcCard>

            {/* Tax card (verokortti) check: one question, one number, a bar comparison. */}
            <JcCard className="p-4 sm:p-5">
              <h2 className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted sm:text-sm">
                <Msg {...messages.taxCardHeading} />
              </h2>
              <p className="mt-1 text-sm text-jc-ink">
                <Msg {...messages.taxCardIntro} values={{ actual: fmtPct((result.breakdown.employee.taxes.total / grossAnnual) * 100) }} />
              </p>
              <div className="mt-3 max-w-xs">
                <PercentField id="tax-iceberg-card-rate" label={formatMessage(messages.taxCardRate)} value={taxCardRate} onChange={setTaxCardRate} />
                <p className="-mt-3 text-[11px] leading-4 text-[#7fa6bd] dark:text-[#6f9cb8]">
                  <Msg {...messages.taxCardWhere} />
                </p>
              </div>
              {taxCardCheck && (
                <div className="mt-2" aria-live="polite">
                  {(() => {
                    const cardPct = taxCardRate ?? 0;
                    const actualPct = taxCardCheck.impliedRatePct;
                    const max = Math.max(cardPct, actualPct, 30);
                    const bar = (label: React.ReactNode, pct: number, color: string) => (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-28 shrink-0 text-jc-muted">{label}</span>
                        <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#e3f2f9] dark:bg-[#123a55]">
                          <span className={`block h-full rounded-full ${color}`} style={{ width: `${(pct / max) * 100}%` }} />
                        </span>
                        <span className="w-12 shrink-0 text-right font-semibold text-jc-ink">{fmtPct(pct)}</span>
                      </div>
                    );
                    return (
                      <div className="space-y-1.5">
                        {bar(<Msg {...messages.taxCardBarCard} />, cardPct, taxCardCheck.verdict === 'backTax' ? 'bg-[#b4532a]' : 'bg-[#1c6493]')}
                        {bar(<Msg {...messages.taxCardBarSalary} />, actualPct, 'bg-[#7fb8d8]')}
                      </div>
                    );
                  })()}
                  <p className={`mt-2 text-sm font-semibold ${taxCardCheck.verdict === 'backTax' ? 'text-[#b4532a]' : 'text-jc-ink'}`}>
                    <Msg
                      {...(taxCardCheck.verdict === 'refund' ? messages.taxCardRefund : taxCardCheck.verdict === 'backTax' ? messages.taxCardBackTax : messages.taxCardOnTarget)}
                      values={{
                        amountYear: fmtCurrency(Math.abs(taxCardCheck.differenceAnnual)),
                        amountMonth: fmtCurrency(Math.abs(taxCardCheck.differenceAnnual) / 12),
                        card: fmtPct(taxCardRate ?? 0),
                      }}
                    />
                  </p>
                </div>
              )}
              <button type="button" onClick={() => setTaxCardLimitOpen((v) => !v)} aria-expanded={taxCardLimitOpen} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1c6493] hover:underline dark:text-[#8fc0dd]">
                <CaretDown className={`h-3.5 w-3.5 transition-transform ${taxCardLimitOpen ? 'rotate-180' : ''}`} />
                <Msg {...messages.taxCardLimitToggle} />
              </button>
              {taxCardLimitOpen && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <Field id="tax-iceberg-card-limit" label={formatMessage(messages.taxCardLimit)} hint={formatMessage(messages.taxCardLimitHint)}>
                    <div className={`${ui.field} h-11`}>
                      <span className={`${ui.prefix} text-sm`}>€</span>
                      <input
                        id="tax-iceberg-card-limit"
                        type="text"
                        inputMode="numeric"
                        value={taxCardLimit !== undefined && taxCardLimit > 0 ? String(taxCardLimit) : ''}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 9);
                          setTaxCardLimit(digits ? Number(digits) : undefined);
                        }}
                        className={`${ui.input} text-sm font-bold`}
                      />
                    </div>
                  </Field>
                  <PercentField id="tax-iceberg-card-extra" label={formatMessage(messages.taxCardExtra)} hint={formatMessage(messages.taxCardExtraHint)} value={taxCardExtraRate} onChange={setTaxCardExtraRate} />
                </div>
              )}
              <p className="mt-3 text-xs text-jc-muted">
                <Msg {...messages.taxCardNote} />
              </p>
            </JcCard>
          </div>
        )}

        {/* The full breakdown: the numbers people came for, right after the answers. */}
        <div>
          {result && (
            <JcCard className="p-4 sm:p-5">
              <div className="mb-3 flex items-baseline justify-between gap-3">

                <h2 className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted sm:text-sm">
                  <Msg {...messages.breakdownHeading} />
                </h2>
                <span className="text-[11px] font-semibold text-jc-muted">
                  <Msg {...(period === 'month' ? messages.amountsPerMonth : messages.amountsPerYear)} />
                </span>
              </div>
              <dl className="divide-y divide-jc-border text-sm">
                <Row label={formatMessage(messages.breakdownGross)} value={fmtAmount(result.salary.grossAnnual)} />
                <Row label={layerLabels.employerContributions} value={fmtAmount(result.salary.employerContributionsTotal)} />
                <Row label={formatMessage(messages.breakdownTrueCost)} value={fmtAmount(result.salary.trueEmployerCost)} emphasize />
                <Row label={layerLabels.employeeTax} value={fmtAmount(result.salary.employeeTax)} />
                <Row label={formatMessage(messages.breakdownNetPay)} value={fmtAmount(result.salary.net)} emphasize={!includeVat} />
                {includeVat && <Row label={layerLabels.vat} value={fmtAmount(result.salary.vat)} />}
                {includeVat && <Row label={layerLabels.netAfterVat} value={fmtAmount(result.salary.netAfterVat)} emphasize />}
                {investments && (
                  <>
                    <Row label={formatMessage(messages.breakdownCapitalGross)} value={fmtAmount(investments.gross)} />
                    <Row label={formatMessage(messages.breakdownInvestmentsTax)} value={fmtAmount(investments.tax)} />
                    {includeVat && <Row label={layerLabels.vat} value={fmtAmount(investments.vat)} />}
                    <Row label={formatMessage(messages.breakdownInvestmentsKeep)} value={fmtAmount(investments.netAfterVat)} emphasize />
                  </>
                )}
              </dl>
            </JcCard>
          )}

        </div>


        {/* The one thing other calculators cannot do: point at the roles and postings this pays. */}
        {result && rolesNear.length > 0 && (
          <JcCard className="mb-4 p-4 sm:p-5">
            <h2 className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted sm:text-sm">
              <Msg {...messages.rolesHeading} values={{ amount: fmtCurrency(Math.round(grossMonthly / 10) * 10) }} />
            </h2>
            <p className="mt-1 text-xs text-jc-muted">
              <Msg {...messages.rolesIntro} />
            </p>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {rolesNear.map((role) => (
                <li key={role.slug}>
                  <Link
                    href={`/salary/${role.slug}`}
                    locale={locale}
                    className="flex h-full flex-col justify-between rounded-xl border border-[#cfe6f3] bg-[#f3fafd] px-3 py-2 transition-colors hover:border-[#3a92c2] dark:border-[#24506f] dark:bg-[#0f2a40]"
                  >
                    <span className="text-sm font-semibold text-jc-ink">{role.title}</span>
                    <span className="mt-1 text-xs text-[#4d7d99] dark:text-[#8fc0dd]">
                      {fmtCurrency(role.median)} · <Msg {...messages.rolesPostings} values={{ count: role.sampleSize }} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link href="/salary" locale={locale} className="mt-3 inline-block text-sm font-medium text-jc-cta underline-offset-2 hover:underline">
              <Msg {...messages.rolesAll} />
            </Link>
          </JcCard>
        )}

        {taxCard && (
          <JcCard className="mt-4 p-4 sm:p-5">
            <h2 className="font-display mb-2 text-lg font-bold text-jc-ink">{taxCard.heading}</h2>
            {taxCard.paragraphs.map((text, i) => (
              <p key={i} className="mt-2 text-sm leading-relaxed text-jc-muted">
                {text}
              </p>
            ))}
          </JcCard>
        )}

        {/* Where it goes: recipient by law, then what the municipality and the state spend on. A bonus
            for the curious, so it sits after the answers people came for. */}
        {result && destinations.length > 0 && (
          <JcCard className="mt-4 p-4 sm:p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h2 className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted sm:text-sm">
                <Msg {...messages.destinationsHeading} />
              </h2>
              <span className="text-[11px] font-semibold text-jc-muted">
                <Msg {...messages.destinationsTotal} values={{ total: fmtAmount(destinationsTotal), period: formatMessage(period === 'year' ? messages.perYearWord : messages.perMonthWord) }} />
              </span>
            </div>
            <p className="mt-1 text-xs text-jc-muted">
              <Msg {...messages.destinationsIntro} />
            </p>
            <ul className="mt-3 space-y-3">
              {destinations.map((d) => {
                const expandable = Boolean(d.breakdown);
                const open = openDestination === d.key;
                const employeePct = d.amount > 0 ? (d.fromEmployee / d.amount) * 100 : 0;
                const row = (
                  <>
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-jc-ink">
                        {destinationCopy[d.key].name}
                        {expandable && <CaretDown className={`h-3.5 w-3.5 text-jc-muted transition-transform ${open ? 'rotate-180' : ''}`} />}
                      </span>
                      <span className="shrink-0 text-sm font-bold text-jc-ink">
                        {fmtAmount(d.amount)} <span className="text-xs font-medium text-jc-muted">{fmtPct(d.sharePct)}</span>
                      </span>
                    </span>
                    <span className="mt-1 block h-2.5 w-full overflow-hidden rounded-full bg-[#e3f2f9] dark:bg-[#123a55]">
                      <span className="flex h-full" style={{ width: `${Math.max(1.5, d.sharePct)}%` }}>
                        <span className="h-full bg-[#1c6493]" style={{ width: `${employeePct}%` }} />
                        <span className="h-full flex-1 bg-[#7fb8d8]" />
                      </span>
                    </span>
                  </>
                );
                return (
                  <li key={d.key}>
                    {expandable ? (
                      <button type="button" onClick={() => setOpenDestination(open ? null : d.key)} aria-expanded={open} className="block w-full rounded-lg text-left">
                        {row}
                      </button>
                    ) : (
                      <div>{row}</div>
                    )}
                    <p className="mt-1 text-xs text-jc-muted">{destinationCopy[d.key].desc}</p>
                    {expandable && open && (
                      <ul className="mt-2 grid gap-x-6 gap-y-1 rounded-xl bg-[#f3fafd] px-3 py-2 sm:grid-cols-2 dark:bg-[#0f2a40]">
                        {d.breakdown!.filter((b) => b.sharePct >= 0.05).map((b) => (
                          <li key={b.key} className="flex items-baseline justify-between gap-3 text-xs">
                            <span className="text-jc-ink">{purposeLabel(b.key, d.key)}</span>
                            <span className="shrink-0 font-semibold text-jc-ink">
                              {fmtAmount(b.amount)} <span className="font-medium text-jc-muted">{fmtPct(b.sharePct)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-jc-muted">
              <Msg {...messages.destinationsLegend} />
            </p>
          </JcCard>
        )}

        {result && (
          <JcCard className="mt-4 p-4 sm:p-5">
            <h2 className="font-display mb-2 text-lg font-bold text-jc-ink">{faqHeading(copyLocale)}</h2>
            <div className="divide-y divide-jc-border">
              {faqItems(copyLocale, {
                gross: perPeriod(result.salary.grossAnnual),
                net: perPeriod(result.salary.net),
                trueEmployerCost: perPeriod(result.salary.trueEmployerCost),
                perYear: period === 'year',
                taxRatePct: (result.breakdown.employee.taxes.total / result.salary.grossAnnual) * 100,
                totalRatePct: result.breakdown.employee.effectiveRatePct,
              }).map((item) => (
                <details key={item.question} className="group py-2">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-1 text-sm font-bold text-jc-ink [&::-webkit-details-marker]:hidden">
                    <span>{item.question}</span>
                    <CaretDown className="h-4 w-4 shrink-0 text-jc-muted transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="pb-2 text-sm leading-relaxed text-jc-muted">{item.answer}</p>
                </details>
              ))}
            </div>
          </JcCard>
        )}

        {/* Crawl paths into the long tail: one page per common monthly salary. */}
        <nav aria-label={formatMessage(messages.popularSalaries)} className="mt-6">
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-jc-muted">
            <Msg {...messages.popularSalaries} />
          </h2>
          <ul className="flex flex-wrap gap-2">
            {POPULAR_MONTHLY_SALARIES.map((amount) => (
              <li key={amount}>
                <Link
                  href={taxIcebergLogicalPath(locale, amount, 'month')}
                  locale={locale}
                  className={`inline-block rounded-full border px-3 py-1 text-sm font-semibold transition-colors ${
                    period === 'month' && amount === grossInput
                      ? 'border-jc-cta bg-jc-cta text-white'
                      : 'border-jc-border bg-jc-surface text-jc-ink hover:border-jc-cta hover:text-jc-cta'
                  }`}
                >
                  <Msg {...messages.popularSalaryLink} values={{ amount: fmtCurrency(amount) }} />
                </Link>
              </li>
            ))}
          </ul>
          <h2 className="mb-2 mt-4 text-xs font-bold uppercase tracking-wide text-jc-muted">
            <Msg {...messages.popularNets} />
          </h2>
          <ul className="flex flex-wrap gap-2">
            {POPULAR_MONTHLY_NETS.map((amount) => (
              <li key={amount}>
                <Link
                  href={taxIcebergLogicalPath(locale, amount, 'month', 'net')}
                  locale={locale}
                  className={`inline-block rounded-full border px-3 py-1 text-sm font-semibold transition-colors ${
                    mode === 'net' && period === 'month' && amount === grossInput
                      ? 'border-jc-cta bg-jc-cta text-white'
                      : 'border-jc-border bg-jc-surface text-jc-ink hover:border-jc-cta hover:text-jc-cta'
                  }`}
                >
                  <Msg {...messages.popularNetLink} values={{ amount: fmtCurrency(amount) }} />
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sources & methodology, last on the page. A native details element: the content is in
            the HTML for crawlers and assistants even while it is collapsed. */}
        <JcCard className="mt-6 overflow-hidden p-0">
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-sm font-bold text-jc-ink transition-colors hover:bg-jc-elevated sm:px-5 sm:text-base [&::-webkit-details-marker]:hidden">
              <span>
                <Msg {...messages.methodologyHeading} />
              </span>
              <CaretDown className="h-4 w-4 shrink-0 text-jc-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <p className="mb-3 text-sm leading-relaxed text-jc-muted">
                  <Msg {...messages.methodologyIntro} />
                </p>
                <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-jc-muted">
                  <li><Msg {...messages.methodologyEmployeeTax} /></li>
                  <li><Msg {...messages.methodologyMunicipal} /></li>
                  <li>
                    <Msg
                      {...messages.methodologyEmployerTyel}
                      values={{ tyelRate: fmtPct(EMPLOYER_COST_PARAMS_2026.tyelEmployerRate * 100) }}
                    />
                  </li>
                  <li>
                    <Msg
                      {...messages.methodologyEmployerOther}
                      values={{
                        unemploymentLow: fmtPct(EMPLOYER_COST_PARAMS_2026.unemploymentInsuranceLowRate * 100),
                        unemploymentHigh: fmtPct(EMPLOYER_COST_PARAMS_2026.unemploymentInsuranceHighRate * 100),
                        healthInsurance: fmtPct(EMPLOYER_COST_PARAMS_2026.healthInsuranceRate * 100),
                      }}
                    />
                  </li>
                  <li>
                    <Msg
                      {...messages.methodologyEmployerAccident}
                      values={{
                        accidentRate: fmtPct(
                          (EMPLOYER_COST_PARAMS_2026.accidentInsuranceAvgRate + EMPLOYER_COST_PARAMS_2026.groupLifeInsuranceAvgRate) * 100,
                        ),
                      }}
                    />
                  </li>
                  <li>
                    <Msg
                      {...messages.methodologyDeductions}
                      values={{
                        ownLiability: fmtCurrency(TAX_PARAMS_2026.commutingOwnLiability),
                        max: fmtCurrency(TAX_PARAMS_2026.commutingDeductionMax),
                        churchRate: fmtPct(CHURCH_TAX_AVG_RATE_PCT),
                      }}
                    />
                  </li>
                  <li>
                    <Msg
                      {...messages.methodologyCapital}
                      values={{
                        lower: fmtPct(CAPITAL_INCOME_PARAMS_2026.lowerRate * 100),
                        upper: fmtPct(CAPITAL_INCOME_PARAMS_2026.upperRate * 100),
                        threshold: fmtCurrency(CAPITAL_INCOME_PARAMS_2026.upperRateThreshold),
                        cap: fmtCurrency(CAPITAL_INCOME_PARAMS_2026.unlistedLightCap),
                      }}
                    />
                  </li>
                  <li><Msg {...messages.methodologyVat} /></li>
                  <li><Msg {...messages.methodologyVatInterpolation} /></li>
                  <li><Msg {...messages.methodologyDestinations} /></li>
                  <li className="italic"><Msg {...messages.methodologyLimitations} /></li>
                </ul>
              <p className="mt-3 text-sm leading-relaxed text-jc-muted">
                <Msg {...messages.accuracyNote} values={{ year: TAX_YEAR }} />
              </p>
              <h3 className="mt-5 text-sm font-bold text-jc-ink">
                <Msg {...messages.sourcesHeading} />
              </h3>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-jc-muted">
                {SOURCES.map((src) => (
                  <li key={src.url}>
                    <a href={src.url} target="_blank" rel="noopener noreferrer" className="font-medium text-jc-cta underline-offset-2 hover:underline">
                      {src.org}: {src.title}
                    </a>
                    {' '}
                    <span>({src.usedFor[copyLocale]})</span>
                  </li>
                ))}
              </ul>
              <h3 id="tax-iceberg-agents" className="mt-5 text-sm font-bold text-jc-ink">
                <Msg {...messages.agentsHeading} />
              </h3>
          <p className="mt-1 break-words text-sm leading-relaxed text-jc-muted">
            <Msg
              {...messages.agentsBody}
              values={{
                urlTemplate: <code className="text-[13px]">{`${BASE_URL}${taxIcebergBasePath(copyLocale)}/${buildTaxIcebergSlug(4000, 'month', copyLocale).replace('4000', '{gross}')}`}</code>,
                apiUrl: <code className="text-[13px]">{`${BASE_URL}/api/tax-iceberg?gross=4000&locale=${copyLocale}`}</code>,
                tool: <code className="text-[13px]">calculate_net_salary_finland</code>,
                connectorUrl: (
                  <a href="/connector" className="underline underline-offset-2">
                    {`${BASE_URL}/connector`}
                  </a>
                ),
              }}
            />
          </p>
            </div>
          </details>
        </JcCard>

        <p className="mt-6 text-sm text-jc-muted">
          <Link href="/salary" locale={locale} className="font-medium text-jc-cta hover:text-jc-cta-hover underline-offset-2 hover:underline">
            <Msg {...messages.backToSalary} />
          </Link>
        </p>
      </div>
    </div>
  );
}


/**
 * One labelled control in the Advanced grid. Every field has the same three-line skeleton
 * (one-line label, 44px control, one-line hint), so fields line up across the four columns no
 * matter which control they hold.
 */
function Field({ id, label, hint, children }: { id?: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <label htmlFor={id} className={`${ui.label} truncate`} title={label}>
        {label}
      </label>
      {children}
      <p className="mt-1 min-h-4 truncate text-[11px] leading-4 text-[#7fa6bd] dark:text-[#6f9cb8]" title={hint}>
        {hint ?? ''}
      </p>
    </div>
  );
}

/** Equal-width option buttons, 44px tall, in the shared blue-and-white style. */
function Segmented<T extends string | boolean | undefined>({ options, value, onChange }: { options: Array<{ value: T; label: string }>; value: T; onChange: (v: T) => void }) {
  return (
    <div className={`${ui.segmented} grid h-11 w-full`} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" onClick={() => onChange(o.value)} aria-pressed={value === o.value} className={`${ui.segment(value === o.value)} h-full truncate px-1.5 text-[11px]`} title={o.label}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Municipality picker: a text field with an in-page list under it holding every municipality
 * (scrollable; typing narrows it). A native <datalist> was tried first, but the browser
 * positions that popup itself and it ended up off screen; this list is a normal element, so it
 * stays under the field and matches the rest of the toolbar. Keyboard: arrows move (the list
 * follows), Enter picks, Escape closes.
 */
function MunicipalityCombobox({ id, value, onChange, placeholder, clearLabel }: { id: string; value: string; onChange: (v: string) => void; placeholder: string; clearLabel: string }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const query = value.trim().toLocaleLowerCase('fi');
  // Every municipality is listed (the list scrolls); typing narrows it, prefix matches first.
  const matches = useMemo(() => {
    if (!query) return MUNICIPALITY_OPTIONS;
    const starts = MUNICIPALITY_OPTIONS.filter((m) => m.toLocaleLowerCase('fi').startsWith(query));
    const contains = MUNICIPALITY_OPTIONS.filter((m) => !m.toLocaleLowerCase('fi').startsWith(query) && m.toLocaleLowerCase('fi').includes(query));
    return [...starts, ...contains];
  }, [query]);
  const listRef = useRef<HTMLUListElement>(null);
  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    const row = listRef.current?.children[highlight] as HTMLElement | undefined;
    row?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);
  const exact = Object.prototype.hasOwnProperty.call(FINNISH_MUNICIPAL_TAX_RATES_2026, value);
  const showList = open && matches.length > 0 && !(exact && matches.length === 1);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <div className={`${ui.field} h-11`}>
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setHighlight((h) => Math.min(matches.length - 1, h + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(0, h - 1));
            } else if (e.key === 'Enter' && showList) {
              e.preventDefault();
              if (matches[highlight]) pick(matches[highlight]);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          className={`${ui.input} text-sm font-semibold`}
        />
        {value ? (
          <button type="button" onClick={() => onChange('')} aria-label={clearLabel} className={ui.iconButton}>
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <CaretDown className="h-4 w-4 shrink-0 text-[#4d7d99]" />
        )}
      </div>
      {showList && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border-2 border-[#bfe0f0] bg-white py-1 shadow-[0_8px_24px_rgba(22,21,15,0.16)] dark:border-[#2a5b80] dark:bg-[#0f2a40]"
        >
          {matches.map((name, i) => (
            <li key={name} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(name)}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm font-semibold ${
                  i === highlight ? 'bg-[#e3f2f9] text-[#1c6493] dark:bg-[#123a55] dark:text-white' : 'text-jc-ink dark:text-[#eaf6fb]'
                }`}
              >
                <span>{name}</span>
                <span className="text-xs font-medium text-[#4d7d99] dark:text-[#8fc0dd]">{FINNISH_MUNICIPAL_TAX_RATES_2026[name].toLocaleString('fi-FI')} %</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The gross salary field: type any amount, or open the list and pick one of the common salaries
 * (each shown with its net at the current options). A plain text input with a numeric keyboard,
 * not a number input: no spinner arrows, no scroll-wheel surprises.
 */
function SalaryCombobox({
  id,
  value,
  onChange,
  presets,
  netFor,
  formatCurrency,
  presetsLabel,
  netText,
  children,
}: {
  id: string;
  value: number;
  onChange: (v: number) => void;
  presets: number[];
  netFor: (gross: number) => number;
  formatCurrency: (v: number) => string;
  presetsLabel: string;
  netText: (net: number) => string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  useEffect(() => {
    if (!open) return;
    // Start on the preset closest to what is typed.
    let best = 0;
    presets.forEach((p, i) => {
      if (Math.abs(p - value) < Math.abs(presets[best]! - value)) best = i;
    });
    setHighlight(best);
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    (listRef.current?.children[highlight] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);
  const pick = (v: number) => {
    onChange(v);
    setOpen(false);
  };
  return (
    <div ref={rootRef} className="relative">
      <div className={`${ui.field} h-11 pr-2`}>
        <span className={`${ui.prefix} font-display text-xl`}>€</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={value > 0 ? String(value) : ''}
          placeholder="0"
          onChange={(e) => {
            const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 9);
            onChange(digits ? Number(digits) : 0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!open) setOpen(true);
              else setHighlight((h) => Math.min(presets.length - 1, h + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(0, h - 1));
            } else if (e.key === 'Enter' && open) {
              e.preventDefault();
              if (presets[highlight] !== undefined) pick(presets[highlight]!);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className={`${ui.input} font-display text-xl font-bold`}
        />
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label={presetsLabel} aria-expanded={open} aria-controls={listId} className={`${ui.iconButton} -ml-1`}>
          <CaretDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {children}
      </div>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={presetsLabel}
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border-2 border-[#bfe0f0] bg-white py-1 shadow-[0_8px_24px_rgba(22,21,15,0.16)] dark:border-[#2a5b80] dark:bg-[#0f2a40]"
        >
          {presets.map((p, i) => (
            <li key={p} role="option" aria-selected={p === value}>
              <button
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(p)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-semibold ${
                  i === highlight ? 'bg-[#e3f2f9] text-[#1c6493] dark:bg-[#123a55] dark:text-white' : 'text-jc-ink dark:text-[#eaf6fb]'
                }`}
              >
                <span className="font-display text-base font-bold">{formatCurrency(p)}</span>
                <span className="text-xs font-medium text-[#4d7d99] dark:text-[#8fc0dd]">{netText(netFor(p))}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A percentage input (tax card rates): decimal text, blank means "not given". */
function PercentField({ id, label, hint, value, onChange }: { id: string; label: string; hint?: string; value: number | undefined; onChange: (v: number | undefined) => void }) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  // A reset from outside (the Reset options button) empties the field.
  useEffect(() => {
    if (value === undefined) setText('');
  }, [value]);
  return (
    <Field id={id} label={label} hint={hint}>
      <div className={`${ui.field} h-11`}>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d.,]/g, '').slice(0, 6);
            setText(raw);
            const n = Number(raw.replace(',', '.'));
            onChange(raw !== '' && Number.isFinite(n) && n >= 0 && n <= 100 ? Math.round(n * 100) / 100 : undefined);
          }}
          className={`${ui.input} text-sm font-bold`}
        />
        <span className={ui.suffix}>%</span>
      </div>
    </Field>
  );
}

/** A euro amount input in the shared blue-and-white style, with a "/ month" or "/ year" suffix. */

function MoneyField({ id, label, hint, value, onChange, suffix }: { id: string; label: string; hint?: string; value: number; onChange: (v: number) => void; suffix: string }) {
  return (
    <Field id={id} label={label} hint={hint}>
      <div className={`${ui.field} h-11`}>
        <span className={`${ui.prefix} text-sm`}>€</span>
        <input
          id={id}
          type="number"
          min={0}
          step={10}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className={`${ui.input} text-sm font-bold`}
        />
        <span className={ui.suffix}>{suffix}</span>
      </div>
    </Field>
  );
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <dt className={emphasize ? 'font-semibold text-jc-ink' : 'text-jc-muted'}>{label}</dt>
      <dd className={emphasize ? 'font-bold text-jc-ink' : 'font-medium text-jc-ink'}>{value}</dd>
    </div>
  );
}
